// The WebGPU duel arena (Three.js WebGPURenderer — no WebGL fallback by design). Renders the FIELD,
// a dark, premium "printed" playmat with clearly framed zone slots and the field cards laid flat on
// the table. The hands + all HUD stay in the DOM (Duel.svelte), layered over this canvas.
//
// Readability over spectacle: the mat is a high-resolution canvas texture (a real playmat print) so
// every zone has a crisp border + label and never washes out. There is NO post-processing — all glow
// is baked into textures (soft radial planes, shadowBlur accents, additive FX); one render pass total.
//
// Card meshes are PERSISTENT and keyed by zone: each board slot owns its mesh and eases in/out when
// its occupant changes, instead of clearing+rebuilding the scene every snapshot. On top of that, a
// decoupled FX layer reacts to duel events (`motion()`) with IN-SCENE flights: summons arc off the
// hand fan onto their zone (turning over mid-air for a Set), draws peel off the deck into the fan,
// relocations fly to their pile, reveals do one smooth horizontal flip, attacks lunge + impact.
// Mid-flight the destination slot is suppressed and the landed mesh "parks" until the next snapshot
// dresses the real slot, so the handoff never blinks or double-shows a card.
//
// Card art is resolved via cardTextureUrl (data.ts) straight from our R2 origin (dev and prod), which
// sends GET * CORS so three.js (crossOrigin=anonymous by default) can upload it to a GPU texture
// untainted. Hover/click are resolved by raycasting the card meshes and handed back via the callbacks.

import * as THREE from "three/webgpu";
import { Fn, uniform, uv, time, vec2, vec3, vec4, fract, floor, sin, cos, dot, exp, length, step, smoothstep, mix, max, clamp } from "three/tsl";
import { POS, LOCATION, extraTopCode, type QueriedCard, type PlayerBoard } from "@ygoevo/protocol";
import { uiHover } from "../uiSfx.ts";
import { cardTextureUrl } from "../../data.ts";
import { drawSleeve, sleeveById, DEFAULT_SLEEVE } from "../duel/sleeves.ts";
import { ATTACK_TIMING, DUR, easeOutExpo, easeOutQuint, easeInOutExpo, pulse } from "../duel/motion.ts";
import { settings, qualityDpr, qualityMaxFps, effectiveTier } from "../stores/settings.svelte.ts";

export interface ArenaCallbacks {
  onHover: (card: QueriedCard | null) => void;
  onClick: (card: QueriedCard, clientX: number, clientY: number) => void;
  /** Manual zone placement: the player clicked a highlighted legal zone. */
  onZoneClick?: (controller: number, location: number, sequence: number) => void;
  /** A deck / graveyard / extra / banished pile was clicked (deck → surrender, piles → view). */
  onPileClick?: (kind: PileKind, mine: boolean) => void;
  /** Empty board (no card / pile / zone) was clicked — used to dismiss the click-to-inspect card detail. */
  onBackdrop?: () => void;
  /** The renderer never became ready (WebGPU init rejected OR hung — Opera-class browsers). The whole
   *  board lives in this canvas, so without this signal a failed init reads as a frozen duel. */
  onInitFail?: () => void;
}

export type PileKind = "deck" | "grave" | "extra" | "removed";

/** A legal zone the player may place into (manual zone-pick mode). */
export interface PlaceTarget { controller: number; location: number; sequence: number }

/** A board zone the attack-aim arc anchors to (controller/location/sequence identify the zone). */
export interface ZoneRef { controller: number; location: number; sequence: number }

/** A live chain link: its 1-based number + the location it activated from (drives the numbered badge)
 *  + the activating card's code (0 = hidden), which the pile levitation shows hovering over the stack. */
export interface ChainLinkRef { link: number; controller: number; location: number; sequence: number; code?: number }

/** A zone's on-screen rectangle (viewport px), so the DOM motion layer can fly cards over the WebGL
 *  board in 3D mode the same way it does over the flat 2D board. */
export interface ArenaScreenRect { left: number; top: number; width: number; height: number }

/** A card-movement reference: which card (controller/location/sequence) on either board. */
interface MoveRef { controller: number; location: number; sequence: number }

/** A board-change flourish, derived from a masked duel event by the store and replayed here. In 3D the
 *  arena owns ALL card motion in-scene (hand→zone summon arcs, deck→fan draws, zone→pile travels, flip
 *  reveals) so cards move through real 3D space; the DOM motion layer keeps travel/draw for the flat 2D
 *  board only, plus the screen-space de-rez dissolve in both renderers. */
export type DuelMotion =
  | { kind: "place"; code: number; controller: number; location: number; sequence: number; summon: "normal" | "special" | "flip" | "set";
      /** Set when the preceding MSG_MOVE already flew this card to the zone — then place only BLOOMS. */
      from?: MoveRef; defense?: boolean;
      /** The arrival flew FACE-DOWN only because the code was masked (opponent's hand play) but the
       *  card is really face-up: turn the landed card over now that the summon event names it. */
      reveal?: boolean;
      /** Extra-deck summon flavour → the type-colored ceremony (portal + cut-in). Absent = plain. */
      extraType?: ExtraSummonType }
  | { kind: "destroy"; code: number; controller: number; location: number; sequence: number; to: MoveRef; battle: boolean }
  // `travel` covers every real relocation INCLUDING onto the field (the MSG_MOVE owns placement
  // flights — it fires at the exact moment the card moves, so flights can never double or lag).
  // `revealAtHand` = a masked hand play whose code the store peeked from the queued naming event:
  // the card FLIPS OVER in the hand first (the reveal), then flies face-up to its zone.
  // `overlay` = an Xyz material attach/detach (locations pre-normalized to the HOST zone): the card
  // shrinks UNDER the host / slides out from beneath it — never a full placement (no park, no hide).
  | { kind: "travel"; code: number; faceDown: boolean; from: MoveRef; to: MoveRef; defense?: boolean; revealAtHand?: boolean; overlay?: boolean }
  | { kind: "draw"; controller: number; cards: number[] } // codes (0 = hidden); your own draws fly face-up
  | { kind: "flip"; code: number; controller: number; location: number; sequence: number; prev: number; now: number }
  | { kind: "attack"; attacker: MoveRef; target: MoveRef | null }
  // `reveal` = the activating card was face-DOWN (a Set trap / Quick-Play flipping up): only THEN is the
  // flip-up reveal correct. A face-up card firing an effect (ignition / continuous / monster) gets a surge.
  | { kind: "activate"; code: number; controller: number; location: number; sequence: number; reveal: boolean }
  // ── intent beats (animation-plan §4.6/§4.4): shuffles void information; equips visibly attach ──
  | { kind: "shuffleHand"; controller: number }
  | { kind: "shuffleDeck"; controller: number }
  | { kind: "shuffleSet"; controller: number; location: number; count: number }
  | { kind: "equip"; from: MoveRef; to: MoveRef }
  // the blow that ends the duel — the renderers hold the moment (slow-mo / vignette)
  | { kind: "lethal"; player: number }
  // chain link N is RESOLVING right now → one bright pulse on its (levitating) card
  | { kind: "resolve"; controller: number; location: number; sequence: number; link: number };

/** Extra-deck summon flavour (drives the type-colored ceremony), read off the card catalog. */
export type ExtraSummonType = "fusion" | "synchro" | "xyz" | "link" | "ritual";

// ── layout (world units; the camera looks down the +z axis at the table) ────────
const CW = 1.12; // card width (x)
const CH = (CW * 86) / 59; // card depth on the table (z) ≈ 1.63
// SQUARE zone sockets (Master-Duel style). Each zone is a square frame that frames the portrait card
// (which still draws at CW×CH). The grid PITCH = ZONE + a small gutter, so the squares tile with clean
// gaps instead of the old touching portrait rectangles. Bump ZONE/PITCH here to resize the whole board.
const ZONE = 1.5;                                        // square zone side
const PITCH = 1.62;                                      // column/row centre spacing (ZONE + gutter)
const COLX = [-2 * PITCH, -PITCH, 0, PITCH, 2 * PITCH];  // 5 zone columns
const Z_MON = 1.55;                                      // monster row distance from centre (your side, +z) — far enough to clear a full-size EMZ card
const Z_ST = Z_MON + PITCH;                              // spell/trap row, one pitch further out
const X_RAIL = 3 * PITCH;                                // the side rail column (field/extra left · banish/grave/deck right)
// The right rail STACKS three piles so banished sits directly ABOVE the graveyard (was an awkward
// extra column off to the side). Left rail mirrors the lower two (field/extra). Compressed so the
// inner (banish) level still clears the centre line.
const RAIL_GAP = 1.16;
const Z_DECK = Z_ST;                    // deck · extra — outermost (nearest you)
const Z_GRAVE = Z_ST - RAIL_GAP;        // graveyard · field — middle
const Z_BANISH = Z_ST - 2 * RAIL_GAP;   // banished — inner, directly above the graveyard (right only)
const Z_HAND = Z_ST + 1.7;                               // off-table hand origin (for fly-ins / fly-outs)
// Extra Monster (Link) Zones — Free Duel only. They sit ON the centre line (z=0), in the 2nd & 4th
// columns. slot()'s point-mirror makes a player's seq 6 and the opponent's seq 5 resolve to the SAME
// world spot there, so the 2 shared zones never double up and never collide with the monster rows.
const Z_EMZ = 0;
const emzX = (sequence: number): number => (sequence === 5 ? COLX[1]! : COLX[3]!);

// Mat half-extents, the printed playmat spans this rect; zones map into it by world→uv.
const MAT_HX = X_RAIL + ZONE * 0.72;
const MAT_HZ = Z_ST + ZONE * 0.72;

// Camera (Master-Duel-style low 3/4 view). CAM_Y/CAM_Z only define the view DIRECTION — the actual
// distance is solved per-resize by fitCamera(), which pulls the camera back until every mat corner
// projects inside the viewport (the board must NEVER be cut off, whatever the canvas aspect). A lower
// CAM_Y / bigger CAM_Z grazes the table more (more perspective drama); ATK/DEF readouts billboard
// camera-facing, so far cards stay legible at any pitch.
const CAM_FOV = 40;
const CAM_Y = 12.6;   // with CAM_Z → ~61° above horizontal (MD-like 3/4 drama, still readable)
const CAM_Z = 7.0;
const CAM_LOOK_Z = 0.2;
// DPR ceiling is now the player's Graphics-quality tier (qualityDpr) — Low 1× / Medium 1.5× / High 2×.
// (The full-viewport board at DPR 2 was the original lag; Medium 1.5× stays the default.)
// fit margins in NDC: |x| ≤ FIT_X, FIT_BOT ≤ y ≤ FIT_TOP (top leaves room for the HUD chips, the
// bottom may run under the screen-anchored hand fan like MD does).
const FIT_X = 0.82, FIT_TOP = 0.90, FIT_BOT = -0.97; // leaves side gutters for the flanking duelist busts

// The HAND is a SCREEN-ANCHORED fan (Master-Duel style), NOT part of the field's perspective. The cards
// are parented to the camera, so they always face the viewer at a constant size and sit pinned to the
// bottom edge (partially clipped, like MD) no matter how top-down the field camera is. Laid out in
// CAMERA-LOCAL space: -z is in front of the camera, +y is up, +x is right.
const HAND_DIST = 6.0;                      // distance in front of the camera (with CAM_FOV → on-screen size)
const HAND_H = 0.6;                          // your card height at HAND_DIST (~14% of screen height, tight MD scale)
const HAND_W = (HAND_H * 59) / 86;
// The CENTRE card's y. The frustum half-height at HAND_DIST is tan(FOV/2)·6 ≈ 2.18. This docks the fan
// into the BOTTOM edge so the cards are slightly clipped by the viewport (MD-style) — the 3D canvas is
// full-viewport (Duel.svelte), so this crop is against the real screen bottom at every window size, not
// a letterboxed canvas edge.
const HAND_BASE_Y = -1.92;
// The fan is PIVOT-based: every card rides an arc of radius HAND_CURVE about a single pivot below the
// hand, so it's always convex ("held out") for any card count — no caving with even counts. HAND_STEP is
// the angle between adjacent cards; HAND_CURVE is the fan radius (bigger = wider, flatter fan).
// Step × curve = the centre-to-centre spacing. Keep it BELOW the card width (HAND_W ≈ 0.41) so the
// cards OVERLAP slightly (MD-style) instead of leaving gaps: 6.4 × 0.052 ≈ 0.33 < 0.41.
const HAND_STEP = 0.052;
const HAND_CURVE = 6.4;                       // flatter fan → side cards droop less, so the smaller cards aren't over-cropped
const OPP_HAND_H = 0.5;                       // opponent's hand is small, just under the top edge (fully visible)
const OPP_HAND_Y = 1.86;                      // centre card is the fan's HIGH point now — top edge 1.86+0.25 < 2.18 frustum
const OPP_HAND_STEP = 0.055;                  // 4.8 × 0.055 ≈ 0.26 < opp card width 0.34 → cards overlap, no gap
const OPP_HAND_CURVE = 4.8;

const CYAN = new THREE.Color(0x16d6f5);
const MAGENTA = new THREE.Color(0xff3b86);
const LIME = new THREE.Color(0x8dff5a);
const GOLD = new THREE.Color(0xffcf52);
const ORANGE = new THREE.Color(0xff8a1e); // "you can act here" glow
const TARGET_MAGENTA = new THREE.Color(0xff2d7e); // "this card was just targeted" (BECOME_TARGET) glow
const GLOW_S = 1.48; // glow plane scale vs the card (a tighter margin — the halo hugs the card)

// ATK/DEF readout: a high-res canvas, scaled per-frame to a CONSTANT on-screen height (px) so it
// reads the same whether a card is near or across the table (a "billboard"-style overlay).
const STAT_TEX_W = 320, STAT_TEX_H = 84;
const STAT_ASPECT = STAT_TEX_W / STAT_TEX_H;
const STAT_PX = 26; // on-screen height of the readout, in CSS px
// Pile-count badges: same white-on-black-outline treatment, also held to a constant on-screen size.
const COUNT_TEX_W = 96, COUNT_TEX_H = 80;
const COUNT_ASPECT = COUNT_TEX_W / COUNT_TEX_H;
const COUNT_PX = 30;
// Chain markers: the CLASSIC treatment — a big golden chain ring slowly rotating flat ON the
// activating card with the link number overlaying its centre (world-sized so it hugs the card).
const CHAIN_RING_D = 1.62;   // ring diameter (world units) — wraps the card
const CHAIN_NUM_S = 0.72;    // number sprite size (world units)
const CHAIN_SPIN = 0.85;     // ring rotation speed (rad/s)

/** A slot position on the table. `mine` flips the side (180° rotation) for the opponent. */
function slot(x: number, z: number, mine: boolean): [number, number] {
  return mine ? [x, z] : [-x, -z];
}

/** Stable per-zone key so a slot's mesh persists across snapshots. */
const slotKey = (side: "me" | "opp", loc: "m" | "s" | "f", i: number): string => `${side}:${loc}:${i}`;

/** Total counters on a card across all counter types (Spell Counters, A-Counters, …). */
function counterTotal(card: QueriedCard): number {
  if (!card.counters) return 0;
  let n = 0;
  for (const k in card.counters) n += card.counters[k] ?? 0;
  return n;
}

type Tween = { t: number; dur: number; ease: (p: number) => number; step: (p: number) => void; done?: () => void };

/** A composable camera-choreography beat (animation-plan §3.1). Every frame the camera is RE-COMPOSED
 *  from camRest: each active beat ADDS its eased displacement to the pose, then the impact shake rides
 *  on top. Beats therefore stack instead of fighting, and the moment the list empties one final compose
 *  puts the camera back at EXACTLY camRest — the "always returns to rest" law is structural, not tuned. */
type CamFx = { t: number; dur: number; apply: (p: number, pos: THREE.Vector3, look: THREE.Vector3) => void };

// Decelerate curves (no overshoot). easeOut[Expo|Quint], easeInOutExpo come from the shared motion
// language (../duel/motion.ts) so the arena and the DOM layer move identically. The old easeOutBack
// (overshoot "pop") is gone — DESIGN.md: "never bounce like a toy".
const easeInCubic = (p: number) => p * p * p;

/** Per-card interaction state from the current prompt (drives the coloured glow border + sword icon).
 *  `special` = an effect activation / Special Summon (magenta); `target` = an on-board pick candidate
 *  (lime); `attackable` = can attack (gold + sword); otherwise a plain play (cyan). */
export interface CardState { selectable: boolean; attackable: boolean; special?: boolean; target?: boolean; targeted?: boolean }

interface SlotMesh {
  group: THREE.Group;
  art: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  glow: THREE.Mesh;
  glowMat: THREE.MeshBasicMaterial;
  frame: THREE.LineSegments;          // crisp bright outline when selectable
  frameMat: THREE.LineBasicMaterial;
  stat: THREE.Sprite;                 // camera-facing ATK/DEF readout
  statMat: THREE.SpriteMaterial;
  sword: THREE.Sprite;                // "can attack" icon (battle phase)
  swordMat: THREE.SpriteMaterial;
  link: THREE.Sprite;                 // equip/target relationship badge (gold chain-link)
  linkMat: THREE.SpriteMaterial;
  x: number;
  z: number;
  mine: boolean;
  sig: string; // card signature so we only rebuild on real change
  occupied: boolean;
  exiting: boolean; // an FX has taken ownership of the visible card; reconciliation must not touch it
  foil: THREE.Mesh; // holographic overlay plane (above the art), visible only for YOUR foil cards
  foilMat: FoilMaterial;
}

// ── Foil finish (3D) ─────────────────────────────────────────────────────────
// A holographic overlay drawn over a card you own a foil of, mirroring the 2D CSS
// tiers: g1 a white shine, g2 a rainbow holo, g3 rainbow + twinkling stars. Drawn
// with additive blending so it only ADDS light in bright bands (a sheen riding on
// the art), dark areas of the band contribute nothing, so the card stays readable.
// Authored in TSL (compiled to WGSL); animated by the built-in `time` node, so the
// loop no longer pushes a uTime uniform per foil per frame.
type FoilMaterial = THREE.MeshBasicNodeMaterial & { uGrade: { value: number } };
function makeFoilMaterial(): FoilMaterial {
  const uGrade = uniform(0);
  const mat = new THREE.MeshBasicNodeMaterial({
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: false,
  }) as FoilMaterial;
  const rgba = Fn(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hash = (p: any) => fract(sin(dot(p, vec2(127.1, 311.7))).mul(43758.5453123));
    const u = uv();
    const diag = u.x.add(u.y).mul(0.5);
    const t = time;
    // moving specular glint, a soft white band (all grades; g1 leans on it)
    const gp = fract(diag.mul(2.0).sub(t.mul(0.32)));
    const glint = exp(gp.sub(0.5).mul(5.0).pow(2.0).negate());
    const col = vec3(glint).mul(mix(0.55, 0.30, step(1.5, uGrade))).toVar();
    // rainbow holographic bands (g2+)
    const rp = fract(diag.mul(3.0).sub(t.mul(0.16)));
    const rc = cos(rp.add(vec3(0.0, 0.33, 0.67)).mul(6.28318)).mul(0.5).add(0.5);
    const bands = sin(diag.mul(26.0).sub(t.mul(1.3))).mul(0.5).add(0.5).mul(0.60).add(0.40);
    col.addAssign(rc.mul(bands.mul(0.30)).mul(step(1.5, uGrade)));
    // twinkling stars (g3), a small round point placed at a random spot within ~10% of cells
    const grid = u.mul(vec2(13.0, 19.0));
    const cell = floor(grid);
    const h = hash(cell);
    const starPos = vec2(hash(cell.add(0.13)), hash(cell.add(0.71)));
    const d = length(fract(grid).sub(starPos));
    const tw = sin(t.mul(3.0).add(h.mul(40.0))).mul(0.5).add(0.5);
    col.addAssign(vec3(1.0).mul(step(0.90, h).mul(smoothstep(0.16, 0.0, d)).mul(tw).mul(1.1)).mul(step(2.5, uGrade)));
    const a = clamp(max(col.r, max(col.g, col.b)), 0.0, 1.0);
    return vec4(col, a);
  })();
  mat.colorNode = rgba.rgb;
  mat.opacityNode = rgba.a;
  mat.uGrade = uGrade; // per-card grade, driven by applyFoil
  return mat;
}

export class DuelArena {
  private renderer: THREE.WebGPURenderer;
  private ready = false; // WebGPU device init is async; no render until it resolves
  private initTimer: ReturnType<typeof setTimeout> | undefined; // init-hang watchdog, cleared on settle/dispose
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private startT = performance.now();
  private lastT = performance.now();
  private raf = 0;
  private disposed = false;
  private osReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // "Reduce motion" is either the OS setting OR the in-app opt-in — read live so the toggle applies
  // mid-duel (the loop and every fx guard read this.reduced each time).
  // `degraded` = the starvation ladder shed GPU load (stage 2) — same gate as reduced-effects.
  private get reduced(): boolean { return this.degraded || this.osReduced || settings.reducedEffects; }

  private matMesh!: THREE.Mesh;
  private slotsGroup = new THREE.Group();   // persistent card slots
  private pilesGroup = new THREE.Group();   // deck / gy / extra / banished stacks (rebuilt each snapshot)
  private handGroup = new THREE.Group();    // in-scene HAND fan (rebuilt each snapshot), raycast for hover/click
  private handMeshes: { mesh: THREE.Mesh; base: THREE.Vector3; foil?: THREE.Mesh; foilMat?: FoilMaterial; key: string; mine: boolean }[] = [];
  private fxGroup = new THREE.Group();      // transient animation meshes
  private zoneGroup = new THREE.Group();    // legal-zone highlights for manual placement (usually hidden)
  private zoneHits = new Map<string, THREE.Mesh>();
  private placeActive = false;
  private slots = new Map<string, SlotMesh>();
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private texCache = new Map<number, THREE.Texture>();
  private myBackTex!: THREE.Texture;
  private oppBackTex!: THREE.Texture;
  private mySleeve = DEFAULT_SLEEVE;
  private oppSleeve = DEFAULT_SLEEVE;
  private tweens: Tween[] = [];
  private shake = 0;                          // symmetric impact noise (destruction) — rides ON TOP of camFx
  // ── the living camera + game-feel clock (animation-plan §3.1/§3.2) ──
  private camFx: CamFx[] = [];                // active camera beats (summon punch-in, attack kick, lethal pull-back)
  private camDirty = false;                   // one extra compose after the last beat → EXACT camRest return
  private freezeLeft = 0;                     // hitstop: REAL-time seconds the tween clock stays frozen (dt=0)
  private timeScale = 1;                      // tween-clock multiplier (lethal slow-mo), driven by slowLeft
  private slowLeft = 0;                       // lethal slow-mo remaining (real-time): 300ms at ×0.25 + 200ms ramp back
  private fxT = 0;                            // pausable FX clock (shake noise phase) — freezes WITH the hitstop
  private camTmpPos = new THREE.Vector3();    // scratch for the per-frame camera compose
  private camTmpLook = new THREE.Vector3();
  private linkKeys = new Set<string>();       // hovered equip/target relationship group ("ctrl|loc|seq")
  private vpH = 600;                          // canvas CSS height (px), drives constant-size readouts
  private statTmp = new THREE.Vector3();      // scratch for the ATK/DEF billboard world position
  private countSprites: THREE.Sprite[] = [];  // pile-count badges (constant-size, rebuilt per snapshot)
  private chainGroup = new THREE.Group();     // classic rotating chain rings over activating cards
  private chainFx = new Map<number, { grp: THREE.Group; ring: THREE.Mesh | THREE.Sprite; bobBase?: number; zoneKey?: string }>(); // by link no.
  /** Field-slot keys whose card LEVITATES while its chain link is live (the loop owns their Y). */
  private chainZoneKeys = new Set<string>();
  private levitated = new Set<string>();
  private chainNumTex = new Map<number, THREE.Texture>(); // cached number texture per link number
  private chainRingTex?: THREE.Texture;       // shared golden chain-ring texture
  // chain drama (§3.4): the whole-mat dim plane + one spotlight light-pool per levitating chain actor
  private chainDim: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial } | null = null;
  private chainDimOn = false;
  private chainPools = new Map<string, { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial }>();
  // extra-deck summon ceremonies (§3.3): the portal pattern texture per summon type, drawn once
  private portalTexCache = new Map<ExtraSummonType, THREE.Texture>();
  // shuffleHand: 0..1 per fan — how far its cards are gathered to the fan centre (the loop blends it)
  private handGather = { me: 0, opp: 0 };
  private gatherTmp = new THREE.Vector3();
  // camera rest pose (solved by fitCamera per resize) + the duel-start dolly state
  private camRest = new THREE.Vector3(0, CAM_Y, CAM_Z);
  private camLook = new THREE.Vector3(0, 0, CAM_LOOK_Z);
  private introActive = false;
  // in-scene card flights: destination slots suppressed mid-flight, the landed mesh "parked" until the
  // next snapshot dresses the real slot (so a card never double-shows or blinks out during the handoff)
  private parked = new Map<string, THREE.Group>();  // slotKey → landed flight mesh awaiting the snapshot
  private handHide = { mine: new Map<number, number>(), opp: 0 }; // fan cards hidden while their flight copy is airborne
  private lastBoard: { me: PlayerBoard; opp: PlayerBoard; seat: number; state: (c: QueriedCard) => CardState } | null = null;
  private lockGroup = new THREE.Group();      // padlock markers on zones a continuous effect shut off
  private lockMask = -1;                       // last applied disabled-field mask (-1 = never applied)
  private lockTex?: THREE.Texture;             // shared padlock glyph texture
  private glowTex?: THREE.Texture;            // shared soft-radial alpha for the "can act" card glow
  private seat = 0;
  private showEmz = false; // render the 2 Extra Monster (Link) Zones (Free Duel only)
  private foilOf: (code: number) => number = () => 0; // your passcode → best owned foil grade (0 = none)

  // attack-aim arc: a persistent curved beam from the declared attacker to the hovered target.
  private aimFrom: ZoneRef | null = null;
  private aimTargetKeys = new Set<string>();
  // zone key ("ctrl:loc:seq") of the card under the cursor → its "can act" glow pops brighter +
  // larger than the others, so the specific TARGET/play you're pointing at stands out.
  private hoverKey: string | null = null;
  private aimLine: THREE.Mesh | null = null;

  constructor(private canvas: HTMLCanvasElement, private cb: ArenaCallbacks, showEmz = false) {
    this.showEmz = showEmz;
    // Honour a LIVE prefers-reduced-motion toggle (the OS setting can change mid-session).
    window.matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", (e) => { this.osReduced = e.matches; });
    // WebGPU only — no WebGL fallback by design. Device init is async; the loop runs from frame one
    // (tweens/board state advance normally) and rendering starts the moment the device is ready.
    this.renderer = new THREE.WebGPURenderer({ canvas, antialias: true, alpha: true, powerPreference: "high-performance" });
    // Capped DPR (re-solved adaptively per resize(), which also bounds the total backing pixels).
    // The cap is the player's Graphics-quality tier (Low 1× is the big win on weak GPUs).
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, qualityDpr(effectiveTier())));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.86;
    // A rejected init falls back to WebGL2 inside three; a HUNG init (spotty WebGPU: adapter exists
    // but the device never comes up) settles nothing — so race a timeout, and on either failure tell
    // the UI via onInitFail so it can flip to the 2D board instead of showing a blank canvas forever.
    let initFailed = false;
    const fail = (err: unknown): void => {
      if (initFailed || this.disposed || this.ready) return;
      initFailed = true;
      this.fellBack = true; // the 2D fallback is firing — no post-init escape hatch may fire it again
      console.error("[arena] renderer init failed — falling back to the 2D board:", err);
      this.cb.onInitFail?.();
    };
    this.initTimer = setTimeout(() => fail(new Error("renderer init timed out (WebGPU device never became ready)")), 6000);
    this.renderer.init().then(
      () => {
        clearTimeout(this.initTimer);
        if (this.disposed) return;
        this.ready = true;
        this.render();
        // ── GPU self-healing (post-incident law): a LOST WebGPU device stops presenting while JS
        // runs on — the board freezes on its last frame with the duel still audible. That must never
        // strand the player: hand the duel to the 2D board the moment the device dies.
        try {
          const dev = (this.renderer as unknown as { backend?: { device?: GPUDevice } }).backend?.device;
          void dev?.lost?.then((info) => {
            if (this.disposed) return;
            console.error("[arena] WebGPU device lost — handing the duel to the 2D board:", info?.message);
            this.failOver();
          });
        } catch { /* backend shape differs across three versions — the render-failure counter still covers us */ }
      },
      (err: unknown) => { clearTimeout(this.initTimer); fail(err); },
    );
    this.resize();

    this.camera = new THREE.PerspectiveCamera(CAM_FOV, this.aspect(), 0.1, 100);
    this.camera.position.set(0, CAM_Y, CAM_Z);
    this.camera.lookAt(this.camLook);
    this.fitCamera();
    this.startIntro();

    // Light barely matters (cards/mat are unlit MeshBasic), a soft ambient just lifts the piles.
    this.scene.add(new THREE.AmbientLight(0x9fb8ff, 1.15));

    this.myBackTex = this.makeBackTexture(this.mySleeve);
    this.oppBackTex = this.makeBackTexture(this.oppSleeve);
    this.buildMat();
    this.scene.add(this.slotsGroup, this.pilesGroup, this.fxGroup, this.zoneGroup, this.chainGroup, this.lockGroup);
    // The hand fan is parented to the CAMERA (screen-anchored), so the camera must live in the scene
    // graph for its children to render.
    this.camera.add(this.handGroup);
    this.scene.add(this.camera);

    // NO post-processing: the UnrealBloomPass chain (5+ blur passes over full-screen render targets
    // EVERY frame) was the single biggest GPU cost — the "super laggy" board. All the glow this scene
    // needs is already BAKED into its textures (soft radial glow planes, shadowBlur'd mat accents,
    // additive FX), so we render the scene directly in one pass.

    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerleave", this.onPointerLeave);
    canvas.addEventListener("click", this.onClick);

    this.loop();
    // arm the rAF-starvation watchdog (the observed prod freeze: rAF dies in a visible tab while
    // timers live on — this interval keeps the board moving when that happens)
    this.starveTimer = setInterval(this.starveCheck, 500);
  }

  private aspect(): number {
    const r = this.canvas.getBoundingClientRect();
    return Math.max(0.1, r.width / Math.max(1, r.height));
  }

  // ── camera framing ─────────────────────────────────────────────────────────────
  /** Solve the camera distance (along the fixed CAM_Y/CAM_Z direction) so the WHOLE mat — corners and
   *  pile tops — projects inside the viewport margins. This is what guarantees the field is never cut
   *  off: the board zooms to fit the canvas aspect instead of assuming one. Bisection, ~20 steps. */
  private fitCamera(): void {
    const dir = new THREE.Vector3(0, CAM_Y, CAM_Z - CAM_LOOK_Z).normalize();
    const pts: THREE.Vector3[] = [];
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) for (const y of [0, 0.6])
      pts.push(new THREE.Vector3(sx * MAT_HX, y, sz * MAT_HZ));
    const v = new THREE.Vector3();
    const fits = (dist: number): boolean => {
      this.camera.position.copy(this.camLook).addScaledVector(dir, dist);
      this.camera.lookAt(this.camLook);
      this.camera.updateMatrixWorld();
      for (const p of pts) {
        v.copy(p).project(this.camera);
        if (Math.abs(v.x) > FIT_X || v.y > FIT_TOP || v.y < FIT_BOT) return false;
      }
      return true;
    };
    let lo = 6, hi = 60;
    for (let i = 0; i < 20; i++) { const mid = (lo + hi) / 2; if (fits(mid)) hi = mid; else lo = mid; }
    this.camRest.copy(this.camLook).addScaledVector(dir, hi);
    // snap to the rest pose unless the intro dolly is mid-sweep (it lerps toward camRest live)
    if (!this.introActive) { this.camera.position.copy(this.camRest); this.camera.lookAt(this.camLook); }
  }

  /** Duel-start dolly: sweep up from a low, close vantage behind your field to the rest pose. The hand
   *  fan is camera-anchored, so it stays pinned on screen while the world sweeps — pure cinema. */
  private startIntro(): void {
    if (this.reduced) return;
    this.introActive = true;
    const lowDir = new THREE.Vector3(0, 4.6, 11.5).normalize();
    const from = this.camLook.clone().addScaledVector(lowDir, this.camRest.distanceTo(this.camLook) * 0.85);
    this.tween(1500, easeInOutExpo, (p) => {
      this.camera.position.lerpVectors(from, this.camRest, p);
      this.camera.lookAt(this.camLook);
    }, () => { this.introActive = false; });
  }

  // ── camera choreography (animation-plan §3.1) ────────────────────────────────
  /** Push a camera beat. Gated HERE so every call site stays clean: reduced motion never moves the
   *  camera, and the intro dolly owns the camera outright until it lands (composing offsets over a
   *  still-moving rest pose would double-drive it). */
  private camBeat(durMs: number, apply: CamFx["apply"]): void {
    if (this.reduced || this.introActive) return;
    this.camFx.push({ t: 0, dur: durMs / 1000, apply });
  }

  /** Rise → (hold) → release envelope: decelerating in, flat hold, decelerating out — the one shape
   *  every camera beat uses, so nothing ever overshoots or snaps (house law: every curve decelerates,
   *  and it must end at EXACTLY 0 so the final compose lands back on camRest). */
  private static env(p: number, up: number, hold = 0): number {
    if (p < up) return easeOutQuint(p / up);
    if (p < up + hold) return 1;
    return 1 - easeInOutExpo((p - up - hold) / Math.max(1e-6, 1 - up - hold));
  }

  /** Summon punch-in: ~7% of the camera distance TOWARD the landing zone (250ms out, ~600ms back). */
  private camPunch(x: number, z: number): void {
    const dir = new THREE.Vector3(x, 0, z).sub(this.camRest).normalize();
    const amp = this.camRest.distanceTo(this.camLook) * 0.07;
    this.camBeat(850, (p, pos) => pos.addScaledVector(dir, amp * DuelArena.env(p, 0.3)));
  }

  /** Attack-impact kick: a DIRECTIONAL hit along the attack vector (vx,vz normalized) — unlike the
   *  symmetric destruction noise, the camera reads the blow's direction. The tiny ramp-in matters: a
   *  hitstop freezes this beat at p=0, so the frozen frame shows the white impact FRAME, not an
   *  already-displaced camera; on release the kick lands and decays out. */
  private camKick(vx: number, vz: number): void {
    this.camBeat(420, (p, pos) => {
      const k = p < 0.12 ? p / 0.12 : 1 - easeOutQuint((p - 0.12) / 0.88);
      pos.x += vx * 0.18 * k;
      pos.z += vz * 0.18 * k;
    });
  }

  /** Freeze the tween clock for `ms` (hitstop, §3.2). Consumed by the LOOP in real time — deliberately
   *  NOT inside advanceTweens, so headless tests stepping the clock manually stay deterministic. */
  hitstop(ms: number): void {
    if (this.reduced || effectiveTier() === "low") return; // a hitstop-extra: low tier keeps flowing
    this.freezeLeft = Math.max(this.freezeLeft, ms / 1000);
  }

  /** Compose the camera for this frame: camRest → sum of active beats → shake noise on top. Runs at
   *  the end of every advanceTweens tick (so headless stepping drives it too); a cheap no-op while
   *  nothing is active. camDirty buys ONE extra compose after the last beat/shake dies, which is what
   *  guarantees the exact camRest/camLook reset. */
  private applyCamera(dt: number): void {
    if (this.introActive) return; // the intro dolly owns the camera until it lands on camRest
    const live = this.camFx.length > 0 || this.shake > 0.001;
    if (!live && !this.camDirty) return;
    const pos = this.camTmpPos.copy(this.camRest);
    const look = this.camTmpLook.copy(this.camLook);
    for (const fx of this.camFx) fx.apply(Math.min(1, fx.t / Math.max(1e-6, fx.dur)), pos, look);
    if (this.shake > 0.001) {
      this.shake *= Math.pow(0.0001, dt); // fast decay (dt-driven: pauses under hitstop, slows in slow-mo)
      const k = this.shake * 0.12;
      pos.x += Math.sin(this.fxT * 60) * k; // fxT (not wall time): the noise phase freezes with the scene
      pos.z += Math.cos(this.fxT * 52) * k;
    }
    this.camera.position.copy(pos);
    this.camera.lookAt(look);
    this.camDirty = live;
  }

  // ── mat (printed playmat texture) ───────────────────────────────────────────────
  /** World (x,z) → canvas px on the mat texture. The texture is flipped vertically when it lands on
   *  the rotateX(-90°) plane (CanvasTexture flipY + the plane's local +y → world -z), so +z (your
   *  near side) must be drawn LOW on the canvas to render at the BOTTOM of the screen, otherwise the
   *  cyan "your" frames print on the far side and you end up sitting on the opponent's red zones. */
  private toMat(x: number, z: number, W: number, H: number): [number, number] {
    const u = (x + MAT_HX) / (2 * MAT_HX);
    const v = (z + MAT_HZ) / (2 * MAT_HZ); // +z (you) → v≈1 (canvas bottom → screen bottom)
    return [u * W, v * H];
  }

  private buildMat(): void {
    const W = 2048, H = Math.round((W * MAT_HZ) / MAT_HX); // keep world aspect
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const g = c.getContext("2d")!;

    // No dark pool / slab, the mat is just the printed ZONES + grid + divider painted straight onto
    // a fully-transparent canvas, so the live cyberspace behind shows through cleanly (a "plain"
    // playmat, per design). The zone frames + their faint inner fills carry all the structure.

    // very subtle carbon-grid texture (low contrast, NOT the old laser grid)
    g.strokeStyle = "rgba(90,150,210,0.045)"; g.lineWidth = 1;
    const step = W / 48;
    g.beginPath();
    for (let gx = 0; gx <= W; gx += step) { g.moveTo(gx, 0); g.lineTo(gx, H); }
    for (let gy = 0; gy <= H; gy += step) { g.moveTo(0, gy); g.lineTo(W, gy); }
    g.stroke();

    const px = (wx: number) => (wx / (2 * MAT_HX)) * W; // world-length → canvas px (x axis)
    const zw = ZONE, zh = ZONE; // square sockets (Master-Duel style)

    const zone = (wx: number, wz: number, mine: boolean, opts: { label?: string; icon?: string; big?: boolean; tint?: string; s?: number } = {}) => {
      const [cx, cy] = this.toMat(wx, wz, W, H);
      const w = px(zw) * (opts.s ?? 1), h = px(zh) * (opts.s ?? 1);
      const tint = opts.tint ?? (mine ? "22,210,245" : "255,70,135");
      this.drawZoneFrame(g, cx, cy, w, h, tint, opts);
      if (opts.label) {
        g.save();
        g.translate(cx, cy);
        // labels read from YOUR side for both halves (single-viewer board — not mirrored)
        g.fillStyle = `rgba(${tint},0.5)`;
        g.font = `600 ${Math.round(h * 0.12)}px Rajdhani, system-ui, sans-serif`;
        g.textAlign = "center"; g.textBaseline = "middle";
        g.fillText(opts.label, 0, opts.icon ? h * 0.04 : 0);
        if (opts.icon) { g.font = `${Math.round(h * 0.26)}px serif`; g.globalAlpha = 0.7; g.fillText(opts.icon, 0, -h * 0.13); g.globalAlpha = 1; }
        g.restore();
      }
    };

    for (const mine of [true, false]) {
      for (let i = 0; i < 5; i++) {
        const [mx, mz] = slot(COLX[i]!, Z_MON, mine);
        zone(mx, mz, mine);
        const [sx, sz] = slot(COLX[i]!, Z_ST, mine);
        zone(sx, sz, mine);
      }
      const [fx, fz] = slot(-X_RAIL, Z_GRAVE, mine); zone(fx, fz, mine, { label: "FIELD" });
      const [ex, ez] = slot(-X_RAIL, Z_DECK, mine); zone(ex, ez, mine, { label: "EXTRA" });
      const [gx, gz] = slot(X_RAIL, Z_GRAVE, mine); zone(gx, gz, mine, { label: "GRAVE" });
      const [dx, dz] = slot(X_RAIL, Z_DECK, mine); zone(dx, dz, mine, { label: "DECK" });
      const [bx, bz] = slot(X_RAIL, Z_BANISH, mine); zone(bx, bz, mine, { label: "BANISH" }); // stacked above the graveyard
    }

    // Free Duel: the 2 shared Extra Monster (Link) Zones, on the centre line, in a violet "EX" tint so
    // they read as distinct from the cyan/magenta player zones. Drawn once (they belong to both sides).
    // Full-size sockets (the monster rows sit far enough out now that the cards clear each other).
    if (this.showEmz) {
      const [lx, lz] = slot(emzX(5), Z_EMZ, true); zone(lx, lz, true, { label: "EX", tint: "176,138,255" });
      const [rx, rz] = slot(emzX(6), Z_EMZ, true); zone(rx, rz, true, { label: "EX", tint: "176,138,255" });
    }

    // centre divider, a thin double rule with a node at the middle.
    const [, midY] = this.toMat(0, 0, W, H);
    const grad = g.createLinearGradient(0, midY, W, midY);
    grad.addColorStop(0, "rgba(22,210,245,0)");
    grad.addColorStop(0.5, "rgba(150,235,255,0.5)");
    grad.addColorStop(1, "rgba(255,70,135,0)");
    g.strokeStyle = grad; g.lineWidth = 3;
    g.beginPath(); g.moveTo(px(2 * MAT_HX) * 0.04, midY); g.lineTo(W - px(2 * MAT_HX) * 0.04, midY); g.stroke();

    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 16; // WebGPU sampler max — keeps the mat print crisp at the shallow camera angle
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.98, depthWrite: false });
    this.matMesh = new THREE.Mesh(new THREE.PlaneGeometry(2 * MAT_HX, 2 * MAT_HZ), mat);
    this.matMesh.rotation.x = -Math.PI / 2;
    this.matMesh.position.set(0, 0, 0);
    this.scene.add(this.matMesh);
  }

  /** One SQUARE octagon zone socket (Master-Duel style): faint inner fill, crisp neon octagon border, a
   *  concentric inner octagon ring, and brighter corner ticks that bloom. The heavy corner chamfer (r)
   *  is what reads as the octagon. */
  private drawZoneFrame(g: CanvasRenderingContext2D, cx: number, cy: number, w: number, h: number, tint: string, opts: { big?: boolean }): void {
    const x = cx - w / 2, y = cy - h / 2, r = Math.min(w, h) * 0.24;
    // an octagon (chamfered square) inset by `pad` from the socket edge
    const octa = (pad: number) => {
      const ax = x + pad, ay = y + pad, aw = w - 2 * pad, ah = h - 2 * pad, ar = Math.min(aw, ah) * 0.24;
      g.beginPath();
      g.moveTo(ax + ar, ay);
      g.lineTo(ax + aw - ar, ay); g.lineTo(ax + aw, ay + ar);
      g.lineTo(ax + aw, ay + ah - ar); g.lineTo(ax + aw - ar, ay + ah);
      g.lineTo(ax + ar, ay + ah); g.lineTo(ax, ay + ah - ar);
      g.lineTo(ax, ay + ar); g.closePath();
    };
    void r;
    // inner fill, a soft top-down gradient, very low alpha
    const fill = g.createLinearGradient(x, y, x, y + h);
    fill.addColorStop(0, `rgba(${tint},0.11)`);
    fill.addColorStop(1, `rgba(${tint},0.03)`);
    octa(0); g.fillStyle = fill; g.fill();
    // outer octagon border (alphas tuned for the DIRECT renderer — no bloom pass brightens these now)
    octa(0); g.strokeStyle = `rgba(${tint},0.55)`; g.lineWidth = 2.5; g.stroke();
    // concentric inner octagon ring (the MD "double frame" read)
    octa(Math.min(w, h) * 0.11); g.strokeStyle = `rgba(${tint},0.25)`; g.lineWidth = 2; g.stroke();
    // corner ticks (brighter accents, these are what bloom catches), pinned to the octagon's chamfers
    g.strokeStyle = `rgba(${tint},0.85)`; g.lineWidth = 3; const t = Math.min(w, h) * 0.14, cr = Math.min(w, h) * 0.24;
    const corner = (ox: number, oy: number, dx: number, dy: number) => { g.beginPath(); g.moveTo(ox, oy + dy * t); g.lineTo(ox, oy); g.lineTo(ox + dx * t, oy); g.stroke(); };
    corner(x + cr, y + 1, 1, 1); corner(x + w - cr, y + 1, -1, 1); corner(x + cr, y + h - 1, 1, -1); corner(x + w - cr, y + h - 1, -1, -1);
  }

  // ── card-back / sleeve texture ──────────────────────────────────────────────────
  private makeBackTexture(id: string): THREE.Texture {
    const def = sleeveById(id);
    if (def.kind === "image" && def.img) {
      // The classic sleeveless card back (or any image sleeve), load the real art, same-origin.
      const t = new THREE.TextureLoader().load(def.img, () => this.render());
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 8;
      return t;
    }
    const c = document.createElement("canvas");
    c.width = 512; c.height = 748; // hand-fan cards render big — a 256px back reads blurry
    drawSleeve(c.getContext("2d")!, id, c.width, c.height);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    return t;
  }

  /** Swap YOUR card-back/sleeve design. Updates every visible back of yours. */
  setSleeve(id: string): void {
    if (!id || id === this.mySleeve) return;
    this.mySleeve = id;
    this.myBackTex.dispose();
    this.myBackTex = this.makeBackTexture(id);
    for (const s of this.slots.values()) if (s.mine && s.sig.includes("|back")) s.sig = "";
  }

  /** Swap the OPPONENT's sleeve (their backs read as theirs, a you/them identity cue). */
  setOppSleeve(id: string): void {
    if (!id || id === this.oppSleeve) return;
    this.oppSleeve = id;
    this.oppBackTex.dispose();
    this.oppBackTex = this.makeBackTexture(id);
    for (const s of this.slots.values()) if (!s.mine && s.sig.includes("|back")) s.sig = "";
  }

  /** Set the resolver mapping a passcode → your best owned foil grade (0 = plain). */
  setFoils(fn: (code: number) => number): void {
    this.foilOf = fn;
  }

  /** Re-evaluate the foil overlay on every occupied slot from its current card (called when
   *  your inventory arrives async, after the board was first drawn). */
  refreshFoils(): void {
    for (const s of this.slots.values()) {
      const card = s.occupied ? (s.art.userData.card as QueriedCard | undefined) : undefined;
      this.applyFoil(s, card ?? null);
    }
    this.render();
  }

  /** Show/hide + grade the holographic overlay for one slot. Only YOUR face-up real cards foil. */
  private applyFoil(s: SlotMesh, card: QueriedCard | null): void {
    let grade = 0;
    if (card && s.mine && card.code) {
      const onField = card.location === LOCATION.MZONE || card.location === LOCATION.SZONE;
      const faceDown = card.code === 0 || (onField && (card.position! & POS.FACEDOWN) !== 0);
      const token = !faceDown && card.type !== undefined && (card.type & 0x4000) !== 0;
      if (!faceDown && !token) grade = this.foilOf(card.code);
    }
    s.foilMat.uGrade.value = grade;
    s.foil.visible = grade > 0;
    s.foil.rotation.z = s.art.rotation.z; // match defense-position / orientation
  }

  // ── textures ──────────────────────────────────────────────────────────────────
  private artTexture(code: number, fallback?: THREE.Texture): THREE.Texture {
    const cached = this.texCache.get(code);
    if (cached) return cached;
    // FULL-RES art (the hand fan + reveals render cards big — the 168px thumb reads soft/blurry).
    // cardTextureUrl folds alt-art / token-duplicate codes to the canonical passcode that
    // actually has art. A 404 (custom cards with no CDN art) must NEVER leave the texture
    // image-less — WebGPU renders that as a SOLID WHITE card (and it reads as a glitch, not a
    // card). Swap in the caller's fallback (tokens) or the shared "missing art" face — the 3D
    // twin of the 2D board's onerror text-frame fallback.
    const t = new THREE.TextureLoader().load(
      cardTextureUrl(code, true),
      () => this.render(),
      undefined,
      () => {
        const fb = fallback?.image ? fallback : this.missingArtTexture();
        t.image = fb.image as typeof t.image;
        t.needsUpdate = true;
        this.render();
      },
    );
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    this.texCache.set(code, t);
    return t;
  }

  // ── persistent card slots ───────────────────────────────────────────────────────
  private ensureSlot(key: string, x: number, z: number, mine: boolean): SlotMesh {
    let s = this.slots.get(key);
    if (s) return s;
    const group = new THREE.Group();
    group.position.set(x, 0.02, z);
    const mat = new THREE.MeshBasicMaterial({ transparent: true });
    const art = new THREE.Mesh(new THREE.PlaneGeometry(CW * 0.95, CH * 0.95), mat);
    art.rotation.x = -Math.PI / 2;
    // Cards render at order 2: OVER the chain field-dim plane / ceremony portals (renderOrder 1, §3.4)
    // which themselves sit over the printed mat (0). Depth still sorts cards among themselves.
    art.renderOrder = 2;
    // Holographic foil overlay, a plane just ABOVE the art (drawn last) shown only for your foils.
    const foilMat = makeFoilMaterial();
    const foil = new THREE.Mesh(new THREE.PlaneGeometry(CW * 0.95, CH * 0.95), foilMat);
    foil.rotation.x = -Math.PI / 2;
    foil.position.y = 0.02;
    foil.renderOrder = 3;
    foil.visible = false;
    // "You can act here" highlight: a SOFT, round orange glow (radial-alpha plane) that haloes the
    // card, no hard rectangle outline. Bright + bloomed so it's easy to see.
    this.glowTex ??= this.makeGlowTexture();
    const glowMat = new THREE.MeshBasicMaterial({ color: ORANGE, map: this.glowTex, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
    const glow = new THREE.Mesh(new THREE.PlaneGeometry(CW * GLOW_S, CH * GLOW_S), glowMat);
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = -0.012;
    glow.renderOrder = 2; // the action glow must survive the chain dim too (it marks targets)
    // (legacy sharp outline kept but never shown, the soft glow replaces it)
    const frameMat = new THREE.LineBasicMaterial({ color: ORANGE, transparent: true, opacity: 0 });
    const frame = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(CW * 1.02, CH * 1.0)), frameMat);
    frame.rotation.x = -Math.PI / 2; frame.position.y = 0.05;
    // camera-facing ATK/DEF readout (only monsters) + a "can attack" sword. Every HUD sprite is
    // depthWrite:false as well as depthTest:false — a transparent sprite still stamps its full QUAD
    // into the depth buffer, culling whatever draws behind it later (the "ATK/DEF label erases the
    // cards behind it" hole).
    const statMat = new THREE.SpriteMaterial({ transparent: true, depthTest: false, depthWrite: false, opacity: 0 });
    const stat = new THREE.Sprite(statMat);
    // Push the readout toward the card's OWN bottom edge (its printed name/stat band) so it never
    // covers the monster art: your cards read at the near edge (+z, lower on screen), the opponent's
    // (art flipped 180° to face them) at the far edge (-z, higher on screen). The number stays a
    // billboard, so it's always readable to you regardless of which way the card faces.
    stat.position.set(0, 0.28, mine ? CH * 0.43 : -CH * 0.43); stat.scale.set(1.0, 0.27, 1); stat.visible = false;
    const swordMat = new THREE.SpriteMaterial({ map: this.swordTexture(), transparent: true, depthTest: false, depthWrite: false, opacity: 0 });
    const sword = new THREE.Sprite(swordMat);
    sword.position.set(0, 0.5, 0); sword.scale.set(0.46, 0.46, 1); sword.visible = false;
    // equip/target relationship badge (a gold chain-link), shown on this card while it's part of the
    // hovered link group. Pinned to the card's near-outer corner so it doesn't fight the sword/stat.
    const linkMat = new THREE.SpriteMaterial({ map: this.linkTexture(), transparent: true, depthTest: false, depthWrite: false, opacity: 0 });
    const link = new THREE.Sprite(linkMat);
    link.position.set(-CW * 0.36, 0.34, CH * 0.34); link.scale.set(0.32, 0.32, 1); link.visible = false;
    group.add(glow, art, foil, frame, stat, sword, link);
    group.visible = false;
    this.slotsGroup.add(group);
    s = { group, art, mat, glow, glowMat, frame, frameMat, stat, statMat, sword, swordMat, link, linkMat, foil, foilMat, x, z, mine, sig: "", occupied: false, exiting: false };
    this.slots.set(key, s);
    return s;
  }

  private _swordTex?: THREE.Texture;
  /** A small gold crossed-swords icon for attackable monsters (drawn once, shared). */
  private swordTexture(): THREE.Texture {
    if (this._swordTex) return this._swordTex;
    const c = document.createElement("canvas"); c.width = 96; c.height = 96;
    const g = c.getContext("2d")!;
    g.strokeStyle = "#ffcf52"; g.lineWidth = 9; g.lineCap = "round";
    g.shadowColor = "rgba(255,180,40,0.9)"; g.shadowBlur = 10;
    // two crossed blades
    g.beginPath(); g.moveTo(22, 78); g.lineTo(74, 22); g.stroke();
    g.beginPath(); g.moveTo(74, 78); g.lineTo(22, 22); g.stroke();
    // hilts
    g.lineWidth = 7;
    g.beginPath(); g.moveTo(14, 70); g.lineTo(30, 86); g.stroke();
    g.beginPath(); g.moveTo(82, 70); g.lineTo(66, 86); g.stroke();
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
    this._swordTex = t; return t;
  }

  private _linkTex?: THREE.Texture;
  /** A small gold chain-link icon (on a dark chip) for the equip/target relationship badge (shared). */
  private linkTexture(): THREE.Texture {
    if (this._linkTex) return this._linkTex;
    const c = document.createElement("canvas"); c.width = 96; c.height = 96;
    const g = c.getContext("2d")!;
    g.fillStyle = "rgba(8,12,22,0.86)";
    g.beginPath(); g.roundRect(8, 8, 80, 80, 20); g.fill();
    // two interlocked rounded links
    g.strokeStyle = "#ffcf52"; g.lineWidth = 8; g.lineCap = "round"; g.lineJoin = "round";
    g.shadowColor = "rgba(255,200,70,0.9)"; g.shadowBlur = 9;
    g.beginPath(); g.roundRect(22, 38, 32, 20, 10); g.stroke();
    g.beginPath(); g.roundRect(42, 38, 32, 20, 10); g.stroke();
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    this._linkTex = t; return t;
  }

  /** A refined glow that WRAPS THE CARD'S RECTANGLE: one wide, very soft halo + a crisp thin edge
   *  right on the card outline. High-res so the falloff is silky — a fat blurry band reads cheap; a
   *  tight line with a long soft tail reads premium. Tinted by the material colour, drawn behind. */
  private makeGlowTexture(): THREE.Texture {
    const W = 360, H = Math.round((W * (CH * GLOW_S)) / (CW * GLOW_S)); // = W * CH/CW
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const g = c.getContext("2d")!;
    // the card rect inside the plane (card = CW×CH centred in CW·GLOW_S × CH·GLOW_S)
    const mx = (W * (1 - 1 / GLOW_S)) / 2, my = (H * (1 - 1 / GLOW_S)) / 2;
    const rx = mx - 2, ry = my - 2, rw = W - 2 * rx, rh = H - 2 * ry, rad = Math.round(W * 0.045);
    g.lineJoin = "round";
    g.shadowColor = "rgba(255,255,255,1)";
    // long soft tail → medium wrap → crisp core, single stroke each (doubling blows it out)
    for (const [blur, lw, a] of [[44, 6, 0.3], [18, 4, 0.7], [4, 2.5, 1]] as const) {
      g.shadowBlur = blur; g.lineWidth = lw;
      g.strokeStyle = `rgba(255,255,255,${a})`;
      g.beginPath(); g.roundRect(rx, ry, rw, rh, rad); g.stroke();
    }
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  /** Build a camera-facing ATK/DEF readout: a single "atk/def" string, bold white with a heavy black
   *  outline (the standard, legible-on-anything treatment). Drawn at high res; the loop scales the
   *  sprite per-frame so it reads at a CONSTANT on-screen size no matter the card's depth. */
  private makeStatTexture(atk: number, def: number): THREE.Texture {
    const fmt = (v: number) => (v === -2 ? "?" : String(v));
    const text = `${fmt(atk)}/${fmt(def)}`;
    const W = STAT_TEX_W, H = STAT_TEX_H;
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const g = c.getContext("2d")!;
    g.font = "800 58px Rajdhani, system-ui, sans-serif";
    g.textAlign = "center"; g.textBaseline = "middle";
    g.lineJoin = "round"; g.miterLimit = 2;
    const cx = W / 2, cy = H / 2 + 2;
    // Near-white, but kept just UNDER the scene's bloom threshold (~0.86 linear) so it stays crisp and
    // legible instead of blooming into an unreadable glow. Heavy black outline carries the contrast.
    g.lineWidth = 10; g.strokeStyle = "#04060c"; g.strokeText(text, cx, cy);
    g.fillStyle = "#dce3ec"; g.fillText(text, cx, cy);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    return t;
  }

  /** A camera-facing counter readout: a gold diamond pip + the total, same heavy-outline treatment as
   *  the ATK/DEF readout (constant on-screen size via the loop). Shown on cards holding counters. */
  private makeCounterTexture(total: number): THREE.Texture {
    const W = STAT_TEX_W, H = STAT_TEX_H;
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const g = c.getContext("2d")!;
    const cy = H / 2;
    const text = String(total);
    g.font = "800 56px Rajdhani, system-ui, sans-serif";
    g.textAlign = "left"; g.textBaseline = "middle";
    const tw = g.measureText(text).width;
    const pip = 30, gap = 12, totalW = pip + gap + tw;
    let x = (W - totalW) / 2;
    // gold diamond pip (a rotated square), black-edged
    g.save();
    g.translate(x + pip / 2, cy); g.rotate(Math.PI / 4);
    g.lineJoin = "round"; g.lineWidth = 8; g.strokeStyle = "#04060c";
    const d = pip - 10; g.strokeRect(-d / 2, -d / 2, d, d);
    g.fillStyle = "#ffcf52"; g.fillRect(-d / 2, -d / 2, d, d);
    g.restore();
    x += pip + gap;
    g.lineJoin = "round"; g.lineWidth = 10; g.strokeStyle = "#04060c"; g.strokeText(text, x, cy + 2);
    g.fillStyle = "#ffcf52"; g.fillText(text, x, cy + 2);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    return t;
  }

  private _missingTex?: THREE.Texture;
  /** The "art missing" card face (custom cards with no CDN art): a dark framed card with a neutral
   *  glyph — clearly a FACE-UP card, never a white slab, never mistaken for a set card back. Shared. */
  private missingArtTexture(): THREE.Texture {
    if (this._missingTex) return this._missingTex;
    const W = 236, H = 344, c = document.createElement("canvas");
    c.width = W; c.height = H;
    const g = c.getContext("2d")!;
    g.fillStyle = "#0d1526"; g.fillRect(0, 0, W, H);
    g.strokeStyle = "rgba(25,224,255,0.45)"; g.lineWidth = 5; g.strokeRect(6, 6, W - 12, H - 12);
    g.fillStyle = "rgba(25,224,255,0.08)"; g.fillRect(20, 44, W - 40, H - 130);
    g.strokeStyle = "rgba(25,224,255,0.35)"; g.lineWidth = 2;
    g.save(); g.translate(W / 2, H * 0.42); g.rotate(Math.PI / 4); g.strokeRect(-34, -34, 68, 68); g.restore();
    g.fillStyle = "#8ea4c8"; g.font = "700 20px Rajdhani, system-ui, sans-serif";
    g.textAlign = "center"; g.textBaseline = "middle";
    g.fillText("NO ART", W / 2, H * 0.82);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    this._missingTex = t; return t;
  }

  private _tokenTex?: THREE.Texture;
  /** A generated "Token" card face. Token monsters have no CDN art (their passcodes 404), so we draw a
   *  clean spectral placeholder, a glowing orb + "TOKEN", instead of a blank/broken card. Shared. */
  private tokenTexture(): THREE.Texture {
    if (this._tokenTex) return this._tokenTex;
    const W = 256, H = 374;
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const g = c.getContext("2d")!;
    const bg = g.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#1b2740"); bg.addColorStop(1, "#0b1120");
    g.fillStyle = bg; g.fillRect(0, 0, W, H);
    const cx = W / 2, cy = H * 0.42, rad = W * 0.3;
    const orb = g.createRadialGradient(cx, cy, 2, cx, cy, rad);
    orb.addColorStop(0, "rgba(190,225,255,0.95)");
    orb.addColorStop(0.5, "rgba(90,170,255,0.5)");
    orb.addColorStop(1, "rgba(60,120,220,0)");
    g.fillStyle = orb; g.beginPath(); g.arc(cx, cy, rad, 0, Math.PI * 2); g.fill();
    g.strokeStyle = "rgba(160,205,255,0.7)"; g.lineWidth = 3;
    g.beginPath(); g.arc(cx, cy, rad * 0.7, 0, Math.PI * 2); g.stroke();
    g.fillStyle = "#cfe3ff"; g.font = "800 30px Rajdhani, system-ui, sans-serif";
    g.textAlign = "center"; g.textBaseline = "middle";
    g.fillText("TOKEN", cx, H * 0.8);
    g.strokeStyle = "rgba(120,170,240,0.5)"; g.lineWidth = 6; g.strokeRect(3, 3, W - 6, H - 6);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    this._tokenTex = t; return t;
  }

  private cardSig(card: QueriedCard, faceDown: boolean, state: CardState, mine: boolean): string {
    const defense = card.location === LOCATION.MZONE && (card.position! & POS.DEFENSE) !== 0;
    const back = `0|back:${mine ? this.mySleeve : this.oppSleeve}`;
    // ATK/DEF only for monsters (cards in the MONSTER zone), spell/traps report 0/0 from the query and
    // must NOT show it. Counter total folds into the sig so the readout refreshes when a counter changes.
    const isMon = card.location === LOCATION.MZONE && card.attack !== undefined && !faceDown;
    const stats = isMon ? `|${card.attack}/${card.defense ?? 0}` : "";
    return `${faceDown ? back : card.code}|${defense ? "d" : "a"}|${card.overlayCount ?? 0}|c${counterTotal(card)}|${state.selectable ? "s" : ""}${state.attackable ? "x" : ""}${state.special ? "e" : ""}${state.target ? "t" : ""}${state.targeted ? "T" : ""}${stats}`;
  }

  /** Apply a card's art + orientation + stat readout to a slot mesh (no animation). */
  private dressSlot(s: SlotMesh, card: QueriedCard, state: CardState): void {
    const onField = card.location === LOCATION.MZONE || card.location === LOCATION.SZONE;
    const masked = card.code === 0;
    const faceDown = masked || (onField && (card.position! & POS.FACEDOWN) !== 0);
    // Tokens (Scapegoat sheep, etc.) show their real token art when we have it; an art-less
    // token falls back to the generated spectral face. TYPE_TOKEN bit = 0x4000.
    const token = !faceDown && card.type !== undefined && (card.type & 0x4000) !== 0;
    s.mat.map = faceDown
      ? (s.mine ? this.myBackTex : this.oppBackTex)
      : this.artTexture(card.code, token ? this.tokenTexture() : undefined);
    s.mat.needsUpdate = true;
    const defense = card.location === LOCATION.MZONE && (card.position! & POS.DEFENSE) !== 0;
    // Physical-table facing: your cards face you, the opponent's face THEM — a 180° spin in the table
    // plane, so they read upside-down from your seat like a real duel across the table. Defense adds the
    // usual 90° on top. Applies to every opponent-owned zone (monster, spell/trap, field, EMZ).
    const rz = this.poseRz(s.mine, defense);
    s.art.rotation.z = rz;
    // The "can act" glow is a rounded-rect that hugs the card outline, spin it WITH the card so a
    // defense-position (sideways) monster's glow lies landscape too, matching the card's orientation
    // (was fixed portrait, so it read wrong on defense monsters). Defense ignores the opponent 180°
    // flip (a rect is symmetric under it) so the glow stays put when only the art faces their way.
    s.glow.rotation.z = defense ? Math.PI / 2 : 0;
    s.art.userData.card = card;
    s.art.userData.selectable = state.selectable;
    s.art.userData.attackable = state.attackable;
    s.art.userData.targeted = state.targeted === true;
    this.applyFoil(s, card); // your face-up foil cards wear their holographic overlay
    // ONE consistent "you can act here" highlight (Master-Duel style): a soft bright-orange glow on
    // every actionable card; the tap menu (and, for monsters, the sword icon) says what the action is.
    // A card an effect just TARGETED (BECOME_TARGET) borrows the same glow in magenta — the 3D arena's
    // equivalent of the 2D board's lock-on reticle, so "which card got hit" reads in both renderers.
    s.glowMat.color.copy(state.targeted ? TARGET_MAGENTA : ORANGE);
    // ATK/DEF readout, face-up MONSTERS only (cards in the monster zone). Spell/traps report 0/0 from
    // the field query, so they must be gated by zone, never shown. A spell/trap holding counters shows
    // its counter total there instead (so the same readout slot stays useful without a fake 0/0).
    const isMon = card.location === LOCATION.MZONE && card.attack !== undefined && !faceDown;
    const ctr = counterTotal(card);
    if (isMon) {
      s.statMat.map?.dispose();
      s.statMat.map = this.makeStatTexture(card.attack!, card.defense ?? 0);
      s.statMat.needsUpdate = true;
      s.stat.visible = true; s.statMat.opacity = 0.96;
    } else if (ctr > 0 && !faceDown) {
      s.statMat.map?.dispose();
      s.statMat.map = this.makeCounterTexture(ctr);
      s.statMat.needsUpdate = true;
      s.stat.visible = true; s.statMat.opacity = 0.96;
    } else {
      s.statMat.map?.dispose(); s.statMat.map = null;
      s.stat.visible = false; s.statMat.opacity = 0;
    }
    s.sword.visible = state.attackable; // opacity pulsed in the loop
  }

  /** Reconcile one slot with the (possibly null) card it should now show. */
  private reconcileSlot(key: string, x: number, z: number, mine: boolean, card: QueriedCard | null, state: CardState): void {
    const s = this.ensureSlot(key, x, z, mine);
    if (s.exiting) return; // an FX owns the visual right now
    if (!card) {
      if (s.occupied) {
        s.occupied = false; s.sig = "";
        s.stat.visible = false; s.sword.visible = false;
        this.tweenOut(s);
      }
      return;
    }
    const onField = card.location === LOCATION.MZONE || card.location === LOCATION.SZONE;
    const masked = card.code === 0;
    const faceDown = masked || (onField && (card.position! & POS.FACEDOWN) !== 0);
    const sig = this.cardSig(card, faceDown, state, mine);
    if (sig === s.sig && s.occupied) return; // unchanged
    const wasOccupied = s.occupied;
    s.sig = sig; s.occupied = true;
    this.dressSlot(s, card, state);
    s.group.visible = true;
    const parkedFx = this.parked.get(key);
    if (parkedFx) {
      // a flight already landed here and has been holding the card's visual — swap it for the real
      // slot mesh seamlessly (full scale, no pop: the card "already arrived")
      this.parked.delete(key);
      this.removeFlight(parkedFx);
      s.group.scale.setScalar(1); s.mat.opacity = 1;
    } else if (!wasOccupied) {
      // pop-in (a card appeared here without a dedicated motion FX)
      this.kill(s.group);
      s.group.scale.setScalar(this.reduced ? 1 : 0.6);
      s.mat.opacity = this.reduced ? 1 : 0.2;
      if (!this.reduced)
        this.tween(DUR.base, easeOutExpo, (p) => { s.group.scale.setScalar(0.6 + 0.4 * p); s.mat.opacity = 0.2 + 0.8 * p; });
    } else {
      s.group.scale.setScalar(1); s.mat.opacity = 1;
    }
  }

  private tweenOut(s: SlotMesh): void {
    if (this.reduced) { s.group.visible = false; return; }
    this.tween(170, easeInCubic, (p) => { s.group.scale.setScalar(1 - 0.4 * p); s.mat.opacity = 1 - p; }, () => {
      if (!s.occupied) { s.group.visible = false; s.group.scale.setScalar(1); s.mat.opacity = 1; }
    });
  }

  private static readonly NO_STATE: CardState = { selectable: false, attackable: false };

  /** Rebuild every field card + pile from the masked board. `state(card)` drives the selectable
   *  glow border + attack sword. */
  setBoard(me: PlayerBoard, opp: PlayerBoard, seat: number, state: (c: QueriedCard) => CardState): void {
    this.seat = seat;
    this.lastBoard = { me, opp, seat, state };
    const st = (c: QueriedCard | null): CardState => (c ? state(c) : DuelArena.NO_STATE);
    for (const [p, mine] of [[me, true], [opp, false]] as const) {
      const side = mine ? "me" : "opp";
      for (let i = 0; i < 5; i++) {
        const [mx, mz] = slot(COLX[i]!, Z_MON, mine);
        const mz0 = p.mzone[i] ?? null;
        this.reconcileSlot(slotKey(side, "m", i), mx, mz, mine, mz0, st(mz0));
        const [sx, sz] = slot(COLX[i]!, Z_ST, mine);
        const sz0 = p.szone[i] ?? null;
        this.reconcileSlot(slotKey(side, "s", i), sx, sz, mine, sz0, st(sz0));
      }
      const [fx, fz] = slot(-X_RAIL, Z_GRAVE, mine);
      const fz0 = p.szone[5] ?? null;
      this.reconcileSlot(slotKey(side, "f", 0), fx, fz, mine, fz0, st(fz0));
      // Free Duel: the 2 Extra Monster Zones (seq 5/6) on the centre line. Each side reconciles its own;
      // shared zones coincide in world space (slot mirror) and only one side ever occupies one.
      if (this.showEmz) {
        for (const i of [5, 6] as const) {
          const [ex, ez] = slot(emzX(i), Z_EMZ, mine);
          const ez0 = p.mzone[i] ?? null;
          this.reconcileSlot(slotKey(side, "m", i), ex, ez, mine, ez0, st(ez0));
        }
      }
    }
    this.buildPiles(me, opp, seat);
    this.buildHand(me, opp);
    this.updateGlows();
    this.applyLinks(); // keep relationship badges correct after the cards move
    this.render();
  }

  // ── screen-anchored HAND fan (parented to the camera) ─────────────────────────
  /** Camera-LOCAL fan transform for the i-th of n hand cards. Yours pins to the bottom edge, the
   *  opponent's (small) to the top. -z is in front of the camera, so the plane's default +z face points
   *  back at the viewer — no lookAt needed. Returns the local position + fan roll. */
  private handCardTransform(i: number, n: number, mine: boolean): { pos: THREE.Vector3; roll: number } {
    const off = i - (n - 1) / 2;
    const step = mine ? HAND_STEP : OPP_HAND_STEP;
    const R = mine ? HAND_CURVE : OPP_HAND_CURVE;
    const baseY = mine ? HAND_BASE_Y : OPP_HAND_Y;
    const theta = off * step; // + = toward the right of the fan
    // Each card rides an arc of radius R about a pivot R below the centre: the centre card sits
    // highest, edges droop + roll tangent — a convex "held-out" fan for ANY count. The SAME shape for
    // BOTH hands: the old opponent mirror (s = -1) flipped the arc AND the roll, which made their fan
    // read upside-down ("looks right only if you rotate the whole thing 180°").
    const pos = new THREE.Vector3(R * Math.sin(theta), baseY - R * (1 - Math.cos(theta)), -HAND_DIST);
    return { pos, roll: -theta };
  }

  /** WORLD position of a hand card (for chain badges anchored to the hand). The hand is camera-local, so
   *  convert through the camera's matrix. */
  private handCardWorld(controller: number, sequence: number): THREE.Vector3 {
    const mine = controller === this.seat;
    const n = Math.max(1, this.lastHand[mine ? "me" : "opp"]);
    const local = this.handCardTransform(Math.min(sequence, n - 1), n, mine).pos.clone();
    this.camera.updateMatrixWorld();
    return this.camera.localToWorld(local);
  }

  private lastHand = { me: 0, opp: 0 }; // hand sizes from the last build (so handCardWorld matches the fan)

  /** Rebuild the fanned hand each snapshot (cheap — textures are cached). YOUR cards show their face +
   *  foil and are raycast-interactive; the opponent's show backs and are inert. */
  private buildHand(me: PlayerBoard, opp: PlayerBoard): void {
    // remember where each card was, so the rebuilt fan GLIDES into the new layout (the loop lerps
    // every card toward its base) instead of teleporting when the hand re-centres after a draw/play
    const oldPos = new Map<string, THREE.Vector3>();
    for (const m of this.handMeshes) oldPos.set(m.key, m.mesh.position.clone());
    for (const m of this.handMeshes) { m.foilMat?.dispose(); (m.mesh.material as THREE.Material).dispose?.(); m.mesh.geometry.dispose(); }
    this.handGroup.clear();
    this.handMeshes = [];
    this.lastHand = { me: me.hand.length, opp: opp.hand.length };
    // cards whose flight copy is currently airborne stay hidden in the fan (consumed per rebuild)
    const hideMine = new Map(this.handHide.mine);
    let hideOpp = this.handHide.opp;
    for (const [hand, mine] of [[me.hand, true], [opp.hand, false]] as const) {
      const n = hand.length;
      const h = mine ? HAND_H : OPP_HAND_H, w = mine ? HAND_W : (OPP_HAND_H * 59) / 86;
      for (let i = 0; i < n; i++) {
        const c = hand[i];
        if (!c) continue;
        const faceDown = c.code === 0; // your hand is face-up to you; the opponent's is masked (code 0)
        const tex = faceDown ? (mine ? this.myBackTex : this.oppBackTex) : this.artTexture(c.code);
        const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthTest: false }); // always over the field
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
        const { pos, roll } = this.handCardTransform(i, n, mine);
        const prev = oldPos.get(`${c.controller}:${c.location}:${c.sequence}`);
        mesh.position.copy(prev ?? pos); // start where the card WAS; the loop glides it to `base`
        mesh.rotation.z = roll;    // camera-local: the plane already faces the viewer, just roll for the fan
        mesh.renderOrder = 20 + i; // hand draws last (over the whole board); later cards overlap earlier
        mesh.userData.ro = mesh.renderOrder; // base draw order (the loop bumps the hovered card above it)
        if (mine && c.code) mesh.userData.card = c; // only your real cards are hover/click targets
        // suppress fan cards whose flight copy is mid-air (yours by code; the opponent's from the end)
        if (mine && c.code && (hideMine.get(c.code) ?? 0) > 0) { hideMine.set(c.code, hideMine.get(c.code)! - 1); mesh.visible = false; }
        else if (!mine && hideOpp > 0 && i >= n - this.handHide.opp) { hideOpp--; mesh.visible = false; }
        this.handGroup.add(mesh);
        const entry: (typeof this.handMeshes)[number] = { mesh, base: pos.clone(), key: `${c.controller}:${c.location}:${c.sequence}`, mine };
        // your face-up foils wear the holographic overlay, same as on the board
        const grade = mine && c.code ? this.foilOf(c.code) : 0;
        if (grade > 0) {
          const foilMat = makeFoilMaterial();
          foilMat.uGrade.value = grade;
          const foil = new THREE.Mesh(new THREE.PlaneGeometry(w, h), foilMat);
          foil.position.copy(mesh.position); foil.rotation.z = roll;
          foil.position.z += 0.01; foil.renderOrder = mesh.renderOrder + 1;
          foil.visible = mesh.visible; // follows a flight-hidden card
          this.handGroup.add(foil);
          entry.foil = foil; entry.foilMat = foilMat;
        }
        this.handMeshes.push(entry);
      }
    }
  }

  /** Set (and show) the hovered equip/target relationship group, `keys` are "ctrl|loc|seq" of every
   *  card in the group, so a chain-link badge lights on each. Pass an empty set to clear. */
  setLinks(keys: Set<string>): void {
    this.linkKeys = keys;
    this.applyLinks();
    this.render();
  }

  private applyLinks(): void {
    for (const s of this.slots.values()) {
      const card = s.art.userData.card as QueriedCard | undefined;
      const on = !!card && s.occupied && !s.exiting && this.linkKeys.size > 0 &&
        this.linkKeys.has(`${card.controller}|${card.location}|${card.sequence}`);
      s.link.visible = on;
      if (!on) s.linkMat.opacity = 0;
    }
  }

  // ── chain markers (the classic rotating chain ring) ─────────────────────────────
  /** Reconcile the live chain: each link wears the CLASSIC marker — a golden ring of chain links slowly
   *  rotating flat ON the activating card, with the link number overlaying the card's centre. Hand
   *  activations get a camera-facing version anchored to the fan card. New links pop in; links that
   *  resolved away (gone from `links`) shrink out and dispose. */
  setChainLinks(links: ChainLinkRef[]): void {
    const want = new Set(links.map((l) => l.link));
    for (const [num, fx] of this.chainFx) {
      if (want.has(num)) continue;                 // still live
      this.chainFx.delete(num);
      // shrink + fade out, then remove (LIFO removal is driven by the store dropping the link)
      const mats: (THREE.SpriteMaterial | THREE.MeshBasicMaterial)[] = [];
      fx.grp.traverse((o) => { const m = (o as THREE.Mesh | THREE.Sprite).material as THREE.SpriteMaterial | undefined; if (m) mats.push(m); });
      this.tween(200, easeInCubic, (p) => { fx.grp.scale.setScalar(Math.max(0.01, 1 - p)); for (const m of mats) m.opacity = 1 - p; }, () => {
        this.chainGroup.remove(fx.grp);
        for (const m of mats) m.dispose();
        fx.grp.traverse((o) => (o as THREE.Mesh).geometry?.dispose?.()); // the pile hover card's plane
      });
    }
    // Field-zone links levitate their card (the loop hovers those slots while the link is live).
    this.chainZoneKeys.clear();
    for (const l of links) {
      if ((l.location & (LOCATION.MZONE | LOCATION.SZONE | LOCATION.FZONE)) === 0) continue;
      const info = this.zoneSlotInfo(l.controller, l.location, l.sequence);
      if (info) this.chainZoneKeys.add(info.key);
    }
    for (const l of links) {
      if (this.chainFx.has(l.link)) continue;
      const onHand = (l.location & LOCATION.HAND) !== 0;
      const onPile = (l.location & (LOCATION.GRAVE | LOCATION.REMOVED | LOCATION.DECK | LOCATION.EXTRA)) !== 0;
      const grp = new THREE.Group();
      let ring: THREE.Mesh | THREE.Sprite;
      const numMat = new THREE.SpriteMaterial({ map: this.chainNumTexture(l.link), transparent: true, depthTest: false, depthWrite: false });
      const num = new THREE.Sprite(numMat);
      if (onPile) {
        // an effect fired FROM a pile (GY / banished / deck / extra): the activating card RISES off the
        // stack and hovers (bobbing in the loop) with the ring + link number riding it — the same
        // "this card is acting" levitation the field zones get.
        const [x, z] = this.zoneWorld(l.controller, l.location, l.sequence);
        const bobBase = 0.85;
        grp.position.set(x, 0.25, z); // starts at the stack; the loop eases it up to its hover height
        const mine = l.controller === this.seat;
        const back = mine ? this.myBackTex : this.oppBackTex;
        const card = this.makeFlightCard(l.code ? this.artTexture(l.code) : back, back).grp;
        this.fxGroup.remove(card); // makeFlightCard parents to the FX layer; this one rides the marker
        card.quaternion.copy(this.flatQ(this.poseRz(mine, false), true));
        card.scale.setScalar(0.92);
        grp.add(card);
        const rm = new THREE.MeshBasicMaterial({ map: this.chainRingTexture(), transparent: true, depthWrite: false });
        ring = new THREE.Mesh(new THREE.PlaneGeometry(CHAIN_RING_D, CHAIN_RING_D), rm);
        ring.rotation.x = -Math.PI / 2;
        ring.renderOrder = 27;
        num.position.y = 0.16;
        num.scale.setScalar(CHAIN_NUM_S);
        num.renderOrder = 28;
        grp.add(ring, num);
        this.chainGroup.add(grp);
        this.chainFx.set(l.link, { grp, ring, bobBase });
        if (!this.reduced) {
          grp.scale.setScalar(0.01);
          this.tween(DUR.base, easeOutExpo, (p) => grp.scale.setScalar(Math.max(0.01, p)), () => grp.scale.setScalar(1));
        }
        continue;
      }
      if (onHand) {
        // fan card: a camera-facing ring (SpriteMaterial.rotation spins it) + the number on top
        grp.position.copy(this.handCardWorld(l.controller, l.sequence));
        const rm = new THREE.SpriteMaterial({ map: this.chainRingTexture(), transparent: true, depthTest: false, depthWrite: false });
        ring = new THREE.Sprite(rm);
        ring.scale.setScalar(0.92);
        ring.renderOrder = 30; // above the screen-anchored hand (renderOrder 20+)
        num.scale.setScalar(0.42);
        num.renderOrder = 31;
      } else {
        // board zone: a dark scrim dims the card art, then the ring LIES ON it (flat mesh) rotating
        // about its own centre — the marker reads at full contrast on ANY art underneath
        const [x, z] = this.zoneWorld(l.controller, l.location, l.sequence);
        grp.position.set(x, 0.06, z);
        const scrim = new THREE.Mesh(
          new THREE.PlaneGeometry(CW * 1.04, CH * 1.02),
          new THREE.MeshBasicMaterial({ color: 0x02040a, transparent: true, opacity: 0.52, depthWrite: false }),
        );
        scrim.rotation.x = -Math.PI / 2;
        scrim.position.y = -0.008;
        scrim.renderOrder = 7;
        grp.add(scrim);
        const rm = new THREE.MeshBasicMaterial({ map: this.chainRingTexture(), transparent: true, depthWrite: false });
        ring = new THREE.Mesh(new THREE.PlaneGeometry(CHAIN_RING_D, CHAIN_RING_D), rm);
        ring.rotation.x = -Math.PI / 2;
        ring.renderOrder = 8;
        num.position.y = 0.16; // the number floats a hair over the ring, always camera-facing
        num.scale.setScalar(CHAIN_NUM_S);
        num.renderOrder = 9;
      }
      grp.add(ring, num);
      this.chainGroup.add(grp);
      // a field-zone marker rides its LEVITATING card: remember the slot so the loop syncs its height
      const zoneKey = !onHand ? this.zoneSlotInfo(l.controller, l.location, l.sequence)?.key : undefined;
      this.chainFx.set(l.link, { grp, ring, ...(zoneKey ? { zoneKey } : {}) });
      if (!this.reduced) {
        grp.scale.setScalar(0.01);
        this.tween(DUR.base, easeOutExpo, (p) => grp.scale.setScalar(Math.max(0.01, p)), () => grp.scale.setScalar(1));
      }
    }
    // §3.4 chain drama: while ANY link is live the table drops away (field dim) and the levitating
    // actors get their light-pools — MD's "everything stops for the effect" read.
    this.setChainDim(links.length > 0);
    this.syncChainPools();
    this.render();
  }

  /** Fade the whole-mat dim plane in/out (~250ms). One persistent mesh (created lazily, disposed with
   *  the arena) at renderOrder 1: over the printed mat (0), under every card (2+) — so the LEVITATING
   *  activator + its reticled targets read spotlit while the table recedes. Subtle: readability first. */
  private setChainDim(on: boolean): void {
    if (this.reduced || effectiveTier() === "low") {
      // heavy-fx gate — and if the tier dropped mid-chain, snap any lingering dim off
      if (this.chainDim) { this.chainDim.mat.opacity = 0; this.chainDim.mesh.visible = false; }
      this.chainDimOn = false;
      return;
    }
    if (on === this.chainDimOn) return;
    this.chainDimOn = on;
    if (!this.chainDim) {
      const mat = new THREE.MeshBasicMaterial({ color: 0x010409, transparent: true, opacity: 0, depthWrite: false });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2 * MAT_HX, 2 * MAT_HZ), mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = 0.015; // over the mat print, under the cards at y 0.02
      mesh.renderOrder = 1;
      mesh.visible = false;
      this.scene.add(mesh);
      this.chainDim = { mesh, mat };
    }
    const { mesh, mat } = this.chainDim;
    const from = mat.opacity, to = on ? 0.42 : 0;
    mesh.visible = true;
    this.tween(250, easeOutQuint, (p) => { mat.opacity = from + (to - from) * p; }, () => { if (mat.opacity < 0.01) mesh.visible = false; });
  }

  /** One soft additive light-pool under each levitating chain actor — the spotlight half of the dim.
   *  Reconciled against chainZoneKeys (pile links carry their own hover card + ring instead). */
  private syncChainPools(): void {
    const want = this.reduced || effectiveTier() === "low" ? new Set<string>() : this.chainZoneKeys;
    for (const [key, pool] of this.chainPools) {
      if (want.has(key)) continue;
      this.chainPools.delete(key);
      const { mesh, mat } = pool;
      const from = mat.opacity;
      this.tween(200, (p) => p, (p) => { mat.opacity = from * (1 - p); }, () => { this.fxGroup.remove(mesh); mesh.geometry.dispose(); mat.dispose(); });
    }
    for (const key of want) {
      if (this.chainPools.has(key)) continue;
      const s = this.slots.get(key);
      if (!s) continue;
      const mat = new THREE.MeshBasicMaterial({ color: 0xbfe9ff, map: this.bloomTexture(), transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(CW * 2.1, CW * 2.1), mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(s.x, 0.018, s.z); // pinned to the zone; the card levitates above it
      mesh.renderOrder = 2;               // over the dim plane — this IS the spotlight
      this.fxGroup.add(mesh);
      this.chainPools.set(key, { mesh, mat });
      this.tween(250, easeOutQuint, (p) => { mat.opacity = 0.5 * p; });
    }
  }

  /** The shared golden chain-ring texture: interlocked links around a circle, alternating a flat link
   *  with an edge-on one (the classic chain read), lightly glowing. Drawn once. */
  private chainRingTexture(): THREE.Texture {
    if (this.chainRingTex) return this.chainRingTex;
    const S = 512;
    const c = document.createElement("canvas"); c.width = c.height = S;
    const g = c.getContext("2d")!;
    const cx = S / 2, cy = S / 2, R = S * 0.4;
    const N = 14;
    g.lineJoin = "round";
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2;
      g.save();
      g.translate(cx + Math.cos(a) * R, cy + Math.sin(a) * R);
      g.rotate(a + Math.PI / 2); // tangent to the circle
      const flat = i % 2 === 0;
      const w = S * 0.088, h = flat ? S * 0.052 : S * 0.024, r = h / 2;
      // near-black under-stroke for depth, then bright gold with a strong glow — high contrast
      g.shadowBlur = 0; g.lineWidth = flat ? 14 : 10; g.strokeStyle = "#1a1002";
      g.beginPath(); g.roundRect(-w / 2, -h / 2, w, h, r); g.stroke();
      g.shadowColor = "rgba(255,214,90,1)"; g.shadowBlur = 13;
      g.lineWidth = flat ? 8 : 5; g.strokeStyle = flat ? "#ffe289" : "#f5c94e";
      g.beginPath(); g.roundRect(-w / 2, -h / 2, w, h, r); g.stroke();
      g.restore();
    }
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    this.chainRingTex = t; return t;
  }

  /** The chain-link NUMBER, big and bold over the card centre: warm gold fill, heavy dark outline and a
   *  soft dark backing so it reads on any card art. Cached per number. */
  private chainNumTexture(n: number): THREE.Texture {
    const cached = this.chainNumTex.get(n); if (cached) return cached;
    const S = 192;
    const c = document.createElement("canvas"); c.width = c.height = S;
    const g = c.getContext("2d")!;
    const cx = S / 2, cy = S / 2;
    // soft dark backing disc so the number never fights the art beneath
    const bg = g.createRadialGradient(cx, cy, 8, cx, cy, S * 0.46);
    bg.addColorStop(0, "rgba(4,6,12,0.72)"); bg.addColorStop(1, "rgba(4,6,12,0)");
    g.fillStyle = bg; g.fillRect(0, 0, S, S);
    g.font = "800 128px Rajdhani, system-ui, sans-serif";
    g.textAlign = "center"; g.textBaseline = "middle";
    g.lineJoin = "round"; g.miterLimit = 2;
    g.lineWidth = 18; g.strokeStyle = "#04060c"; g.strokeText(String(n), cx, cy + 6);
    g.shadowColor = "rgba(255,200,70,0.8)"; g.shadowBlur = 12;
    g.fillStyle = "#ffe9b0"; g.fillText(String(n), cx, cy + 6);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    this.chainNumTex.set(n, t); return t;
  }

  private updateGlows(): void {
    for (const s of this.slots.values()) {
      // A targeted card glows too (magenta, set in dressSlot), even though it isn't a play candidate.
      const sel = (s.art.userData.selectable === true || s.art.userData.targeted === true) && s.occupied && !s.exiting;
      s.glowMat.opacity = sel ? 1 : 0;        // bright; pulsed in the render loop
      s.frameMat.opacity = 0;                 // no hard outline, the soft glow carries it
      if (s.art.userData.attackable !== true) s.swordMat.opacity = 0;
    }
  }

  // ── manual zone placement ─────────────────────────────────────────────────────
  /** Light up the legal zones for a SELECT_PLACE and accept clicks on them (manual zone-pick mode).
   *  Pass null to clear. Already-chosen zones can be excluded by the caller. */
  setPlaceTargets(targets: PlaceTarget[] | null): void {
    this.zoneGroup.clear();
    this.zoneHits.clear();
    this.placeActive = !!targets && targets.length > 0;
    if (!targets) { this.render(); return; }
    for (const t of targets) {
      const [x, z] = this.zoneWorld(t.controller, t.location, t.sequence);
      const mat = new THREE.MeshBasicMaterial({ color: LIME, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
      const m = new THREE.Mesh(new THREE.PlaneGeometry(CW * 0.98, CH * 0.98), mat);
      m.rotation.x = -Math.PI / 2;
      m.position.set(x, 0.05, z);
      m.renderOrder = 2; // legal-zone picks must stay readable over the chain field-dim
      m.userData.zone = t;
      this.zoneGroup.add(m);
      this.zoneHits.set(`${t.controller}:${t.location}:${t.sequence}`, m);
    }
    this.canvas.style.cursor = "pointer";
    this.render();
  }

  // ── locked zones (Ground Collapse, etc.) ────────────────────────────────────────
  /** Mark the zones a continuous effect has shut off with a magenta padlock + hazard fill. `mask` is
   *  the raw OCGcore disabled-field bitmask in ABSOLUTE player coords (controller c: MZONE seq s → bit
   *  c*16+s; SZONE seq s → bit c*16+8+s). Pass 0 to clear. */
  setDisabledZones(mask: number): void {
    if (mask === this.lockMask) return;
    this.lockMask = mask;
    this.lockGroup.clear();
    for (let c = 0; c < 2 && mask; c++) {
      const base = c * 16;
      for (let s = 0; s < 7; s++) // monster zones (5,6 are the EMZ — only present in Free Duel)
        if ((this.showEmz || s < 5) && (mask & (1 << (base + s)))) this.addLockMarker(c, LOCATION.MZONE, s);
      for (let s = 0; s < 5; s++) // spell/trap zones
        if (mask & (1 << (base + 8 + s))) this.addLockMarker(c, LOCATION.SZONE, s);
    }
    this.render();
  }

  private addLockMarker(controller: number, location: number, sequence: number): void {
    const [x, z] = this.zoneWorld(controller, location, sequence);
    // magenta tint on the mat (plain alpha, NOT additive — reads as "blocked", unlike the lime place glow)
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(CW * 0.98, CH * 0.98),
      new THREE.MeshBasicMaterial({ color: MAGENTA, transparent: true, opacity: 0.18, depthWrite: false, side: THREE.DoubleSide }),
    );
    plane.rotation.x = -Math.PI / 2;
    plane.position.set(x, 0.04, z);
    this.lockGroup.add(plane);
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.lockTexture(), transparent: true, depthTest: false, depthWrite: false, opacity: 0.95 }));
    spr.position.set(x, 0.32, z);
    spr.scale.setScalar(0.55);
    spr.renderOrder = 6;
    this.lockGroup.add(spr);
  }

  /** A magenta padlock glyph (shared, cached) for the locked-zone marker. */
  private lockTexture(): THREE.Texture {
    if (this.lockTex) return this.lockTex;
    const S = 128, c = document.createElement("canvas"); c.width = c.height = S;
    const g = c.getContext("2d")!;
    g.lineCap = "round"; g.lineJoin = "round";
    g.shadowColor = "rgba(255,59,134,0.95)"; g.shadowBlur = 12;
    g.lineWidth = 13; g.strokeStyle = "#ff7ab0";        // shackle
    g.beginPath(); g.arc(64, 52, 21, Math.PI, 0); g.stroke();
    g.shadowBlur = 0;
    g.fillStyle = "#ff3b86"; g.beginPath(); g.roundRect(34, 52, 60, 48, 9); g.fill(); // body
    g.lineWidth = 4; g.strokeStyle = "#2a0512"; g.stroke();
    g.fillStyle = "#2a0512"; g.beginPath(); g.arc(64, 71, 7, 0, Math.PI * 2); g.fill(); g.fillRect(61, 71, 6, 17); // keyhole
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    this.lockTex = t; return t;
  }

  // ── attack-aim arc ─────────────────────────────────────────────────────────────
  /** Begin/refresh the attack-aim arc. `from` is the declared attacker's zone; `targets` are the
   *  legal target zones, the arc snaps to whichever the pointer is over. Pass from=null to clear. */
  setAim(from: ZoneRef | null, targets: ZoneRef[]): void {
    this.aimFrom = from;
    this.aimTargetKeys = new Set(targets.map((t) => `${t.controller}:${t.location}:${t.sequence}`));
    if (!from) this.clearAimLine();
    this.render();
  }

  private clearAimLine(): void {
    if (!this.aimLine) return;
    this.fxGroup.remove(this.aimLine);
    (this.aimLine.geometry as THREE.BufferGeometry).dispose();
    (this.aimLine.material as THREE.Material).dispose();
    this.aimLine = null;
  }

  /** Draw/refresh the persistent gold arc from the attacker to the hovered target zone. */
  private drawAim(target: ZoneRef): void {
    if (!this.aimFrom || this.reduced) return;
    const [ax, az] = this.zoneWorld(this.aimFrom.controller, this.aimFrom.location, this.aimFrom.sequence);
    const [tx, tz] = this.zoneWorld(target.controller, target.location, target.sequence);
    const start = new THREE.Vector3(ax, 0.14, az);
    const end = new THREE.Vector3(tx, 0.14, tz);
    const lift = Math.hypot(tx - ax, tz - az) * 0.3 + 0.6;
    const mid = new THREE.Vector3((ax + tx) / 2, 0.14 + lift, (az + tz) / 2);
    const geo = new THREE.TubeGeometry(new THREE.QuadraticBezierCurve3(start, mid, end), 24, 0.05, 8, false);
    if (!this.aimLine) {
      const mat = new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity: 0.92, blending: THREE.AdditiveBlending, depthWrite: false });
      this.aimLine = new THREE.Mesh(geo, mat);
      this.fxGroup.add(this.aimLine);
    } else {
      (this.aimLine.geometry as THREE.BufferGeometry).dispose();
      this.aimLine.geometry = geo;
    }
  }

  // ── piles (deck / gy / extra / banished), cheap, rebuilt each snapshot ────────────
  private buildPiles(me: PlayerBoard, opp: PlayerBoard, seat: number): void {
    this.pilesGroup.clear();
    this.countSprites = [];
    for (const [p, mine, col] of [[me, true, CYAN], [opp, false, MAGENTA]] as const) {
      const gy = this.pileMesh(p.grave.length, p.grave.at(-1)?.code ?? 0, ...slot(X_RAIL, Z_GRAVE, mine), col, mine, "grave"); if (gy) this.pilesGroup.add(gy);
      const dk = this.pileMesh(p.deckCount, 0, ...slot(X_RAIL, Z_DECK, mine), col, mine, "deck"); if (dk) this.pilesGroup.add(dk);
      const ex = this.pileMesh(p.extraCount, extraTopCode(p), ...slot(-X_RAIL, Z_DECK, mine), col, mine, "extra"); if (ex) this.pilesGroup.add(ex);
      const bn = this.pileMesh(p.removed.length, p.removed.at(-1)?.code ?? 0, ...slot(X_RAIL, Z_BANISH, mine), col, mine, "removed"); if (bn) this.pilesGroup.add(bn);
    }
    void seat;
  }

  private pileMesh(count: number, topCode: number, x: number, z: number, color: THREE.Color, mine: boolean, kind: PileKind): THREE.Object3D | null {
    if (count <= 0) return null;
    const grp = new THREE.Group();
    grp.userData.pile = { kind, mine };   // raycast target for view / surrender
    const w = CW * 0.9, h = CH * 0.9;
    const height = Math.min(0.5, 0.035 + count * 0.012);
    const body = new THREE.Mesh(new THREE.BoxGeometry(w, height, h), new THREE.MeshBasicMaterial({ color: 0x0a1326 }));
    body.position.set(x, height / 2, z);
    grp.add(body);
    const rim = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(w, height, h)), new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.35 }));
    rim.position.copy(body.position);
    grp.add(rim);
    const top = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ map: topCode ? this.artTexture(topCode) : (mine ? this.myBackTex : this.oppBackTex), transparent: true }));
    top.rotation.x = -Math.PI / 2;
    top.rotation.z = mine ? 0 : Math.PI; // opponent's pile tops (grave / banish / extra) face THEM too
    top.position.set(x, height + 0.006, z);
    grp.add(top);
    // Count floats CENTRED over the pile.
    const cs = this.countSprite(count, x, z, height + 0.14, color);
    this.countSprites.push(cs);
    grp.add(cs);
    // Dedicated raycast target (the ONLY thing pickPile hits, the decorative body/rim/count never
    // steal a click). Each plane covers its own pile's footprint and sits right on its own stack top, so
    // the pile UNDER the cursor is exactly the one picked. GY / banished get a slightly generous pad
    // (easy to open the viewer); deck / extra match their visible top so clicking the deck reliably
    // lands on the deck (it used to lose overlapping clicks to a deprioritised view pile).
    const pad = (kind === "grave" || kind === "removed") ? 1.16 : 1.0;
    const hit = new THREE.Mesh(
      new THREE.PlaneGeometry(w * pad, h * pad),
      new THREE.MeshBasicMaterial({ visible: false, depthWrite: false }),
    );
    hit.rotation.x = -Math.PI / 2;
    hit.position.set(x, height + 0.02, z); // right on the stack top, so the hit area matches the pile
    hit.userData.pileHit = true;           // mark as the canonical hit target
    grp.add(hit);
    return grp;
  }

  private countSprite(n: number, x: number, z: number, y: number, _color: THREE.Color): THREE.Sprite {
    const W = COUNT_TEX_W, H = COUNT_TEX_H;
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const g = c.getContext("2d")!;
    // SAME treatment as the ATK/DEF readout: bare number, heavy black outline, near-white fill kept
    // just under the scene bloom threshold so it reads crisp instead of glowing. No chip.
    g.font = "800 56px Rajdhani, system-ui, sans-serif"; g.textAlign = "center"; g.textBaseline = "middle";
    g.lineJoin = "round"; g.lineWidth = 11; g.strokeStyle = "#04060c";
    g.strokeText(String(n), W / 2, H / 2 + 2);
    g.fillStyle = "#dce3ec"; g.fillText(String(n), W / 2, H / 2 + 2);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: t, transparent: true, depthTest: false, depthWrite: false }));
    s.position.set(x, y, z); s.renderOrder = 12; // always on top, never occluded by a card
    s.scale.set(0.46 * COUNT_ASPECT, 0.46, 1);   // overridden per-frame for a constant on-screen size
    return s;
  }

  // ── zone world-coordinate lookup (for event FX) ──────────────────────────────────
  private zoneWorld(controller: number, location: number, sequence: number): [number, number] {
    const mine = controller === this.seat;
    if (location & LOCATION.MZONE) {
      if (this.showEmz && sequence >= 5) return slot(emzX(sequence), Z_EMZ, mine);
      return slot(COLX[Math.min(sequence, 4)] ?? 0, Z_MON, mine);
    }
    if (location & LOCATION.SZONE) {
      if (sequence >= 5) return slot(-X_RAIL, Z_GRAVE, mine); // field zone
      return slot(COLX[Math.min(sequence, 4)] ?? 0, Z_ST, mine);
    }
    if (location & LOCATION.FZONE) return slot(-X_RAIL, Z_GRAVE, mine);
    if (location & LOCATION.GRAVE) return slot(X_RAIL, Z_GRAVE, mine);
    if (location & LOCATION.REMOVED) return slot(X_RAIL, Z_BANISH, mine);
    if (location & LOCATION.DECK) return slot(X_RAIL, Z_DECK, mine);
    if (location & LOCATION.EXTRA) return slot(-X_RAIL, Z_DECK, mine);
    return [0, mine ? Z_HAND : -Z_HAND]; // hand / unknown
  }

  /** A field zone's persistent-slot identity: key + world spot + side. Null for non-field locations. */
  private zoneSlotInfo(controller: number, location: number, sequence: number): { key: string; x: number; z: number; mine: boolean } | null {
    const mine = controller === this.seat;
    const side = mine ? "me" : "opp";
    const [x, z] = this.zoneWorld(controller, location, sequence);
    if (location & LOCATION.MZONE) return { key: slotKey(side, "m", this.showEmz ? sequence : Math.min(sequence, 4)), x, z, mine };
    if (location & LOCATION.SZONE) return sequence >= 5 ? { key: slotKey(side, "f", 0), x, z, mine } : { key: slotKey(side, "s", Math.min(sequence, 4)), x, z, mine };
    if (location & LOCATION.FZONE) return { key: slotKey(side, "f", 0), x, z, mine };
    return null;
  }

  private slotForZone(controller: number, location: number, sequence: number): SlotMesh | undefined {
    const info = this.zoneSlotInfo(controller, location, sequence);
    return info ? this.slots.get(info.key) : undefined;
  }

  // ── in-scene card flights ─────────────────────────────────────────────────────────
  // The cinematic backbone: a real two-sided card mesh (face art in front, sleeve behind) that arcs
  // through 3D space between any two poses. Because it's genuinely two-sided, a flight from your hand
  // to a face-down Set NATURALLY shows the card turning over — no texture-swap tricks, no fake spins.

  /** THE card-pose law (rulebook model): a card on the table is face-up or face-down (flatQ's flip)
   *  × vertical or horizontal (defense) × facing its OWNER (opponent cards read upside-down from your
   *  seat — the settled physical-table convention). Every flight, flip and slot dress derives its
   *  table rotation from THIS, so a flight can never land in a pose the slot won't dress it in. */
  private poseRz(mine: boolean, defense: boolean): number {
    return (defense ? Math.PI / 2 : 0) + (mine ? 0 : Math.PI);
  }

  /** Orientation of a card lying flat on the table (matches dressSlot's art pose). `faceUp:false`
   *  flips it about the card's own long axis — the smooth HORIZONTAL flip, like turning a real card. */
  private flatQ(rz: number, faceUp: boolean): THREE.Quaternion {
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, rz));
    if (!faceUp) q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI));
    return q;
  }

  /** Build the two-sided flight card (front = face art, back = sleeve). Added to the FX layer. */
  private makeFlightCard(face: THREE.Texture, back: THREE.Texture): { grp: THREE.Group; mats: THREE.MeshBasicMaterial[] } {
    const grp = new THREE.Group();
    const geo = new THREE.PlaneGeometry(CW * 0.95, CH * 0.95);
    const fm = new THREE.MeshBasicMaterial({ map: face, transparent: true, depthTest: false });
    const bm = new THREE.MeshBasicMaterial({ map: back, transparent: true, depthTest: false });
    const f = new THREE.Mesh(geo, fm); f.renderOrder = 26;
    const b = new THREE.Mesh(geo, bm); b.rotation.y = Math.PI; b.renderOrder = 26;
    grp.add(f, b);
    this.fxGroup.add(grp);
    return { grp, mats: [fm, bm] };
  }

  private removeFlight(grp: THREE.Group): void {
    this.fxGroup.remove(grp);
    (grp.children[0] as THREE.Mesh | undefined)?.geometry.dispose(); // geometry shared by both faces
    grp.traverse((o) => ((o as THREE.Mesh).material as THREE.Material | undefined)?.dispose?.());
  }

  /** Fly a card from one pose to another along a lifted arc, slerping its orientation (which is what
   *  makes reveals/conceals read as real card flips). `to`/`toQ` may be THUNKS re-evaluated per frame,
   *  so a flight into the (camera-anchored) hand fan tracks the fan even while the camera is moving
   *  (duel-start dolly, impact shake). Returns the mesh so the caller can park it. */
  private flyCard(o: {
    face: THREE.Texture; back: THREE.Texture;
    from: THREE.Vector3; fromQ: THREE.Quaternion; fromS?: number;
    to: THREE.Vector3 | (() => THREE.Vector3); toQ: THREE.Quaternion | (() => THREE.Quaternion); toS?: number;
    arc?: number; dur?: number; delay?: number; ease?: (p: number) => number;
    /** Flip the card over IN PLACE (back → face, ms) before the flight — the "reveal in hand" beat. */
    preFlip?: number;
    fadeOut?: boolean; removeOnLand?: boolean; onLand?: (grp: THREE.Group) => void;
  }): THREE.Group {
    const { grp, mats } = this.makeFlightCard(o.face, o.back);
    const fromS = o.fromS ?? 1, toS = o.toS ?? 1;
    const toV = () => (typeof o.to === "function" ? o.to() : o.to);
    const toQv = () => (typeof o.toQ === "function" ? o.toQ() : o.toQ);
    grp.position.copy(o.from); grp.quaternion.copy(o.fromQ); grp.scale.setScalar(fromS);
    if (o.delay) grp.visible = false;
    const run = () => {
      grp.visible = true;
      this.tween(o.dur ?? 560, o.ease ?? easeInOutExpo, (p) => {
        const c = toV();
        const u = 1 - p;
        const mx = (o.from.x + c.x) / 2, my = (o.from.y + c.y) / 2 + (o.arc ?? 0), mz = (o.from.z + c.z) / 2;
        grp.position.set(
          u * u * o.from.x + 2 * u * p * mx + p * p * c.x,
          u * u * o.from.y + 2 * u * p * my + p * p * c.y,
          u * u * o.from.z + 2 * u * p * mz + p * p * c.z,
        );
        grp.quaternion.slerpQuaternions(o.fromQ, toQv(), p);
        grp.scale.setScalar(fromS + (toS - fromS) * p);
        if (o.fadeOut && p > 0.8) { const k = 1 - (p - 0.8) / 0.2; for (const m of mats) m.opacity = k; }
      }, () => {
        o.onLand?.(grp);
        if (o.fadeOut || o.removeOnLand) this.removeFlight(grp);
      });
    };
    if (o.preFlip) {
      // the reveal beat: start with the BACK toward the viewer at the source pose (an opponent's fan
      // card), turn over in place to show the face, THEN fly. A real player showing you the card.
      const yFlip = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
      const q0 = o.fromQ.clone().multiply(yFlip);
      grp.quaternion.copy(q0);
      const start = () => this.tween(o.preFlip!, easeInOutExpo, (p) => {
        grp.quaternion.slerpQuaternions(q0, o.fromQ, p);
        grp.scale.setScalar(fromS * (1 + 0.1 * Math.sin(p * Math.PI))); // a slight presentation swell
      }, run);
      if (o.delay) this.tweenDelay(o.delay, () => { grp.visible = true; start(); }); else start();
    } else if (o.delay) this.tweenDelay(o.delay, run);
    else run();
    return grp;
  }

  // ── per-zone FX guard ─────────────────────────────────────────────────────────
  // The engine narrates one physical act through several messages (MOVE + SUMMONING, CHAINING +
  // POS_CHANGE…). Whichever motion animates a zone first claims it briefly; a second animation for the
  // same zone inside the window is dropped — the root cause of "it flies in, teleports back, then
  // animates again". The board handoff still happens (the guard only suppresses the THEATRE).
  private zoneFxUntil = new Map<string, number>();
  private claimZoneFx(controller: number, location: number, sequence: number, ms: number, take = false): boolean {
    const key = `${controller}:${location}:${sequence}`;
    const now = performance.now();
    if (!take && (this.zoneFxUntil.get(key) ?? 0) > now) return false;
    this.zoneFxUntil.set(key, now + ms);
    return true;
  }

  /** Hide a field slot while a flight owns its card's visual (reconciliation skips it meanwhile).
   *  Also consumes any PARKED flight mesh still holding this zone — the new FX owns the visual now,
   *  so the old landed copy must go (else a reveal flip would play over a lingering card back). */
  private suppressZone(controller: number, location: number, sequence: number): void {
    const info = this.zoneSlotInfo(controller, location, sequence);
    if (!info) return;
    const parkedFx = this.parked.get(info.key);
    if (parkedFx) { this.parked.delete(info.key); this.removeFlight(parkedFx); }
    const s = this.ensureSlot(info.key, info.x, info.z, info.mine);
    s.exiting = true; s.group.visible = false;
  }

  /** A flight landed on a field zone: park the mesh there and hand the visual back to the slot. The
   *  next reconciliation (from the stored snapshot now, or the real one when it arrives) dresses the
   *  slot at full scale and consumes the parked mesh — no blink, no double-card. */
  private landFlight(grp: THREE.Group, controller: number, location: number, sequence: number): void {
    const info = this.zoneSlotInfo(controller, location, sequence);
    if (!info) { this.removeFlight(grp); return; }
    const s = this.slots.get(info.key);
    if (s) { s.exiting = false; s.occupied = false; s.sig = ""; }
    // Never orphan an earlier handoff: if two flights raced to the same zone (engine re-narration
    // outside the claim window), the second landing replaces the first's parked mesh — without this,
    // the overwritten mesh stayed in the FX group forever (its sweep no-ops once it's not the one
    // parked). Leak-proof by construction, not by timing.
    const prev = this.parked.get(info.key);
    if (prev && prev !== grp) this.removeFlight(prev);
    this.parked.set(info.key, grp);
    // safety: if no snapshot ever dresses this zone (e.g. the card was negated away), sweep the mesh
    this.tweenDelay(1600, () => {
      if (this.parked.get(info.key) === grp) { this.parked.delete(info.key); this.removeFlight(grp); this.refreshBoard(); }
    });
    this.refreshBoard();
  }

  /** Re-run reconciliation from the last snapshot (post-flight handoffs). */
  private refreshBoard(): void {
    const b = this.lastBoard;
    if (b) this.setBoard(b.me, b.opp, b.seat, b.state);
  }

  /** Resolve where a card leaves the player's hand from: the actual fan mesh when we can find it (yours,
   *  by passcode), else the fan's end. Hides the fan copy for the duration of the flight. */
  private handSource(controller: number, code: number): { pos: THREE.Vector3; q: THREE.Quaternion; s: number; release: () => void } {
    const mine = controller === this.seat;
    if (mine && code) {
      const hm = this.handMeshes.find((h) => (h.mesh.userData.card as QueriedCard | undefined)?.code === code && h.mesh.visible);
      if (hm) {
        hm.mesh.visible = false;
        if (hm.foil) hm.foil.visible = false;
        this.handHide.mine.set(code, (this.handHide.mine.get(code) ?? 0) + 1);
        return {
          pos: hm.mesh.getWorldPosition(new THREE.Vector3()),
          q: hm.mesh.getWorldQuaternion(new THREE.Quaternion()),
          s: HAND_H / (CH * 0.95),
          release: () => { this.handHide.mine.set(code, Math.max(0, (this.handHide.mine.get(code) ?? 1) - 1)); },
        };
      }
    }
    // the opponent's hand is masked (or the mesh wasn't found): lift the last fan card
    if (!mine) {
      const oppMeshes = this.handMeshes.filter((h) => !h.mesh.userData.card);
      const last = oppMeshes.at(-1);
      if (last && last.mesh.visible) { last.mesh.visible = false; this.handHide.opp++; }
      const pos = last ? last.mesh.getWorldPosition(new THREE.Vector3()) : this.camera.localToWorld(new THREE.Vector3(0, OPP_HAND_Y, -HAND_DIST));
      return {
        pos, q: this.camera.quaternion.clone(), s: OPP_HAND_H / (CH * 0.95),
        release: () => { this.handHide.opp = Math.max(0, this.handHide.opp - 1); },
      };
    }
    const t = this.handCardTransform(Math.max(0, this.lastHand.me - 1), Math.max(1, this.lastHand.me), true);
    return { pos: this.camera.localToWorld(t.pos.clone()), q: this.camera.quaternion.clone(), s: HAND_H / (CH * 0.95), release: () => {} };
  }

  // ── event-driven FX ──────────────────────────────────────────────────────────────
  /** Replay a board-change flourish derived from a duel event. */
  motion(m: DuelMotion): void {
    switch (m.kind) {
      case "place": this.fxPlace(m); break;
      case "destroy": this.fxDestroy(m); break;
      case "travel": this.fxTravel(m); break;
      case "flip": this.fxFlip(m); break;
      case "attack": this.fxAttack(m); break;
      case "activate": this.fxActivate(m); break;
      case "draw": this.fxDraw(m); break; // cards peel off the deck and arc into the fan, in-scene
      // the Master-Duel pass (animation-plan §3):
      case "shuffleHand": this.fxShuffleHand(m); break; // fan gathers to a stack + re-deals
      case "shuffleDeck": this.fxShuffleDeck(m); break; // pile riffle wiggle
      case "shuffleSet": this.fxShuffleSet(m); break;   // the three-card monte on the set row
      case "equip": this.fxEquip(m); break;             // gold beam from equip card to host
      case "lethal": this.fxLethal(); break;            // slow-mo + held camera before the end screen
      case "resolve": this.fxResolve(m); break;         // pulse the levitating card as its link fires
    }
    this.render();
  }

  /** A zone's on-screen rect (viewport px), so the DOM motion layer can fly cards over the 3D board.
   *  Projects the zone's world centre through the camera and sizes the card from a second projected
   *  point one half-card deep, so a near and a far zone both get a correctly-scaled card rect. */
  screenRectFor(controller: number, location: number, sequence: number): ArenaScreenRect | null {
    const [x, z] = this.zoneWorld(controller, location, sequence);
    const r = this.canvas.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const toScreen = (wx: number, wy: number, wz: number): [number, number] => {
      const p = new THREE.Vector3(wx, wy, wz).project(this.camera);
      return [r.left + (p.x * 0.5 + 0.5) * r.width, r.top + (-p.y * 0.5 + 0.5) * r.height];
    };
    const [cx, cy] = toScreen(x, 0.02, z);
    const [, ey] = toScreen(x, 0.02, z - CH / 2); // a point one half-card "up" the table → on-screen card height
    const h = Math.max(24, Math.abs(ey - cy) * 2);
    const w = h * (CW / CH);
    return { left: cx - w / 2, top: cy - h / 2, width: w, height: h };
  }

  private fxPlace(m: Extract<DuelMotion, { kind: "place" }>): void {
    const [x, z] = this.zoneWorld(m.controller, m.location, m.sequence);
    // Neon summon palette: normal = cyan (the structural accent), special = lime (rare tertiary), set =
    // dim cyan-blue. Gold is reserved for DP (DESIGN.md) — it never marks a summon. An extra-deck
    // summon takes its TYPE's ceremony colour instead (fusion violet, synchro white, …) and a bigger
    // bloom — the §3.3 hierarchy: set < normal < special < ceremony.
    if (m.extraType) this.ceremony(x, z, m.extraType); // portal opens NOW, the card lands through it
    const color = m.extraType ? new THREE.Color(DuelArena.PORTAL[m.extraType].bloom)
      : m.summon === "set" ? new THREE.Color(0x5fa8ff) : m.summon === "special" ? LIME : CYAN;
    const strength = m.extraType ? 1.7 : m.summon === "set" ? 0.6 : m.summon === "special" ? 1.3 : 1;
    const bloom = () => {
      this.summonFlash(x, z, color, strength);
      if (m.summon !== "set" && !this.reduced) {
        this.shake = Math.min(1, this.shake + 0.25);
        this.camPunch(x, z); // §3.1: the camera leans into every monster arrival (sets stay quiet)
      }
    };
    if (m.summon === "flip") {
      // flip summon: the card is ALREADY on the field face-down — turn it over, don't fly anything
      this.flipOver(m.controller, m.location, m.sequence, m.code, true, !!m.defense);
      this.summonFlash(x, z, color, 1);
      this.camPunch(x, z);
      return;
    }
    // `from` set = the MSG_MOVE flight already carried the card here a beat ago — this event is just
    // the summon MOMENT. Normally bloom + impact only, never a second flight; but a MASKED arrival
    // (opponent's hand play — the flight flew a card BACK) whose card is really face-up turns over
    // NOW, when this event finally names it: the reveal reads as intended, not an instant art swap.
    if (m.from || this.reduced) {
      if (m.from && m.reveal && !this.reduced)
        this.flipOver(m.controller, m.location, m.sequence, m.code, true, !!m.defense, true);
      bloom();
      return;
    }
    // No move was seen (token summon / engine quirk): fly it ourselves — if the zone is unclaimed.
    if (!this.claimZoneFx(m.controller, m.location, m.sequence, 900)) { bloom(); return; }
    const isMZ = (m.location & LOCATION.MZONE) !== 0;
    this.summonFlight({
      code: m.code, controller: m.controller, location: m.location, sequence: m.sequence,
      faceUp: m.summon !== "set",
      defense: m.summon === "set" ? isMZ : !!m.defense, // set monsters lie sideways; set S/T stay portrait
      preferHand: m.summon !== "special", // normal summons + sets always come from the hand
      color, strength,
      shake: m.summon === "set" ? 0 : 0.25,
    });
  }

  /** The Master-Duel summon beat: the card FLIES to its zone — off the hand fan (turning over on the
   *  way if it's a Set) or materialising down out of the air for an unknown special summon — then
   *  lands into the bloom. Only used when no MSG_MOVE narrated the placement (fxTravel owns that). */
  private summonFlight(o: {
    code: number; controller: number; location: number; sequence: number;
    faceUp: boolean; defense: boolean; preferHand: boolean;
    color: THREE.Color; strength: number; shake: number;
  }): void {
    const [x, z] = this.zoneWorld(o.controller, o.location, o.sequence);
    const mine = o.controller === this.seat;
    const rz = this.poseRz(mine, o.defense); // land EXACTLY as the slot will dress it — no snap on handoff
    const to = new THREE.Vector3(x, 0.02, z);
    const toQ = this.flatQ(rz, o.faceUp);
    const back = mine ? this.myBackTex : this.oppBackTex;
    const face = o.code ? this.artTexture(o.code) : back;
    const inMyHand = mine && !!o.code &&
      this.handMeshes.some((h) => (h.mesh.userData.card as QueriedCard | undefined)?.code === o.code && h.mesh.visible);
    let from: THREE.Vector3, fromQ: THREE.Quaternion, fromS = 1, arc = 1.0, release = () => {};
    if (o.preferHand || inMyHand) {
      const h = this.handSource(o.controller, o.code);
      from = h.pos; fromQ = h.q; fromS = h.s; release = h.release;
      arc = 1.1;
    } else {
      // unknown source (a masked special summon): the card MATERIALISES — drops in facing the viewer
      // and twists down flat into the zone, like MD's light-drop.
      from = new THREE.Vector3(x, 2.9, z);
      fromQ = this.camera.quaternion.clone();
      fromS = 0.72; arc = 0;
    }
    this.suppressZone(o.controller, o.location, o.sequence);
    this.flyCard({
      face, back, from, fromQ, fromS, to, toQ, arc, dur: DUR.hold,
      onLand: (grp) => {
        release();
        this.landFlight(grp, o.controller, o.location, o.sequence);
        this.summonFlash(x, z, o.color, o.strength);
        if (o.shake > 0) {
          this.shake = Math.min(1, this.shake + o.shake);
          this.camPunch(x, z); // same landing-weight punch as the bloom-only path (sets: shake=0, quiet)
        }
      },
    });
  }

  private _bloomTex?: THREE.Texture;
  /** A soft radial light falloff (no geometric edges — hard rings read cheap). Shared. */
  private bloomTexture(): THREE.Texture {
    if (this._bloomTex) return this._bloomTex;
    const S = 256, c = document.createElement("canvas"); c.width = c.height = S;
    const g = c.getContext("2d")!;
    const grad = g.createRadialGradient(S / 2, S / 2, 2, S / 2, S / 2, S / 2);
    grad.addColorStop(0, "rgba(255,255,255,0.95)");
    grad.addColorStop(0.25, "rgba(255,255,255,0.5)");
    grad.addColorStop(0.6, "rgba(255,255,255,0.14)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = grad; g.fillRect(0, 0, S, S);
    const t = new THREE.CanvasTexture(c);
    this._bloomTex = t; return t;
  }

  private _shaftTex?: THREE.Texture;
  /** Vertical light-shaft falloff: bright at the table, dissolving to nothing at the top. Shared. */
  private shaftTexture(): THREE.Texture {
    if (this._shaftTex) return this._shaftTex;
    const W = 64, H = 256, c = document.createElement("canvas"); c.width = W; c.height = H;
    const g = c.getContext("2d")!;
    const grad = g.createLinearGradient(0, H, 0, 0);
    grad.addColorStop(0, "rgba(255,255,255,0.85)");
    grad.addColorStop(0.4, "rgba(255,255,255,0.3)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = grad; g.fillRect(0, 0, W, H);
    const t = new THREE.CanvasTexture(c);
    this._shaftTex = t; return t;
  }

  /** The summon/activation bloom: a soft pool of light blooming on the mat + a column of light that
   *  rises and dissolves + a few motes. All texture-falloff, no hard geometry — light, not vectors. */
  private summonFlash(x: number, z: number, color: THREE.Color, strength: number): void {
    if (this.reduced) return;
    const poolMat = new THREE.MeshBasicMaterial({ color, map: this.bloomTexture(), transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
    const pool = new THREE.Mesh(new THREE.PlaneGeometry(CW * 2.6, CW * 2.6), poolMat);
    pool.rotation.x = -Math.PI / 2; pool.position.set(x, 0.045, z);
    const shaftMat = new THREE.MeshBasicMaterial({ color, map: this.shaftTexture(), transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(CW * 0.4, CW * 0.5, 2.1, 24, 1, true), shaftMat);
    shaft.position.set(x, 1.05, z);
    this.fxGroup.add(pool, shaft);
    this.tween(DUR.hold + 120, (p) => p, (p) => {
      pool.scale.setScalar(0.55 + easeOutQuint(p) * 0.85);
      poolMat.opacity = Math.sin(Math.min(1, p * 1.25) * Math.PI) * 0.95 * strength;
      shaftMat.opacity = Math.sin(Math.min(1, p * 1.45) * Math.PI) * 0.52 * strength;
      shaft.scale.set(1 - p * 0.35, 0.45 + easeOutQuint(p) * 0.75, 1 - p * 0.35); // narrows as it rises
      shaft.rotation.y = p * 0.9;
    }, () => { this.fxGroup.remove(pool, shaft); pool.geometry.dispose(); poolMat.dispose(); shaft.geometry.dispose(); shaftMat.dispose(); });
    if (strength >= 1) this.sparks(x, 0.1, z, color, 7);
  }

  // ── extra-deck summon ceremonies (§3.3) ──────────────────────────────────────────
  /** Ceremony palette per summon type: `tint` paints the portal pattern, `bloom` colours the landing
   *  flash. All inside the neon house palette — no warm explosion colours. */
  private static readonly PORTAL: Record<ExtraSummonType, { tint: string; bloom: number }> = {
    fusion: { tint: "#b96bff", bloom: 0xb96bff },  // violet swirl
    synchro: { tint: "#eaf8ff", bloom: 0xd9f2ff }, // white/cyan concentric rings
    xyz: { tint: "#f2f4ff", bloom: 0xcfd4e6 },     // void-dark spiral, white rim
    link: { tint: "#19e0ff", bloom: 0x19e0ff },    // cyan circuit traces
    ritual: { tint: "#2c49ff", bloom: 0x4a66ff },  // deep-blue glyph ring
  };

  /** The portal PATTERN, drawn once per type and cached (disposed with the arena). Pure canvas — no
   *  shader pass, the animation is the two quads counter-rotating/scaling in ceremony(). */
  private portalTexture(type: ExtraSummonType): THREE.Texture {
    const cached = this.portalTexCache.get(type);
    if (cached) return cached;
    const S = 512, c = document.createElement("canvas");
    c.width = c.height = S;
    const g = c.getContext("2d")!;
    const R = S * 0.46, tint = DuelArena.PORTAL[type].tint;
    g.translate(S / 2, S / 2);
    g.lineCap = "round";
    const ring = (r: number, lw: number, alpha: number, blur = 10) => {
      g.shadowColor = tint; g.shadowBlur = blur;
      g.strokeStyle = tint; g.globalAlpha = alpha; g.lineWidth = lw;
      g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.stroke();
      g.globalAlpha = 1;
    };
    if (type === "fusion" || type === "xyz") {
      // spiral arms (fusion = violet swirl; xyz = white rim spiral OVER a void-dark disc)
      if (type === "xyz") { g.shadowBlur = 0; g.fillStyle = "rgba(17,19,29,0.94)"; g.beginPath(); g.arc(0, 0, R, 0, Math.PI * 2); g.fill(); }
      g.shadowColor = tint; g.shadowBlur = 12; g.strokeStyle = tint;
      for (let arm = 0; arm < 3; arm++) {
        g.globalAlpha = type === "xyz" ? 0.75 : 0.85;
        g.lineWidth = 7;
        g.beginPath();
        for (let i = 0; i <= 40; i++) {
          const t = i / 40;
          const a = arm * ((Math.PI * 2) / 3) + t * 4.6;
          const r = R * (0.12 + 0.82 * t);
          const px2 = Math.cos(a) * r, py2 = Math.sin(a) * r;
          if (i === 0) g.moveTo(px2, py2); else g.lineTo(px2, py2);
        }
        g.stroke();
        g.globalAlpha = 1;
      }
      ring(R * 0.98, type === "xyz" ? 6 : 3, type === "xyz" ? 1 : 0.6, 14); // the rim
    } else if (type === "synchro") {
      // concentric rings — the tuning-rings read
      for (const [r, lw, a] of [[0.3, 3, 0.9], [0.52, 5, 0.75], [0.74, 3, 0.6], [0.95, 6, 0.95]] as const) ring(R * r, lw, a, 12);
    } else if (type === "link") {
      // circuit traces: radial lines with a right-angle jog + square node pads
      ring(R * 0.98, 4, 0.85, 12);
      g.shadowColor = tint; g.shadowBlur = 9; g.strokeStyle = tint; g.fillStyle = tint;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + 0.18;
        g.save(); g.rotate(a);
        g.globalAlpha = 0.8; g.lineWidth = 4;
        const jog = (i % 2 === 0 ? 1 : -1) * R * 0.1;
        g.beginPath(); g.moveTo(R * 0.16, 0); g.lineTo(R * 0.5, 0); g.lineTo(R * 0.5, jog); g.lineTo(R * 0.86, jog); g.stroke();
        g.fillRect(R * 0.86 - 7, jog - 7, 14, 14); // node pad
        g.globalAlpha = 1;
        g.restore();
      }
    } else {
      // ritual: a glyph ring — tick runes between two rings
      ring(R * 0.6, 3, 0.7, 10);
      ring(R * 0.96, 5, 0.9, 12);
      g.shadowColor = tint; g.shadowBlur = 8; g.strokeStyle = tint;
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        g.save(); g.rotate(a);
        g.globalAlpha = 0.85; g.lineWidth = i % 4 === 0 ? 6 : 3;
        g.beginPath(); g.moveTo(R * 0.66, 0); g.lineTo(R * (i % 2 === 0 ? 0.9 : 0.82), 0); g.stroke();
        g.globalAlpha = 1;
        g.restore();
      }
    }
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 4;
    this.portalTexCache.set(type, t);
    return t;
  }

  /** The extra-deck PORTAL: two stacked counter-rotating pattern quads grow in under the zone (~250ms),
   *  the card lands through them, and the portal collapses (~300ms). Additive — light, not paint —
   *  EXCEPT the Xyz base, whose void-dark disc must OCCLUDE the mat print (normal blending) for the
   *  white rim to read. renderOrder 1: over the mat (0), under every card (2+). */
  private ceremony(x: number, z: number, type: ExtraSummonType): void {
    if (this.reduced || effectiveTier() === "low") return; // ceremony is the priciest fx — low tier skips
    const tex = this.portalTexture(type);
    const mk = (size: number, blending: THREE.Blending, y: number) => {
      const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0, blending, depthWrite: false });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(x, y, z);
      mesh.renderOrder = 1;
      this.fxGroup.add(mesh);
      return { mesh, mat };
    };
    const base = mk(2.15, type === "xyz" ? THREE.NormalBlending : THREE.AdditiveBlending, 0.028);
    const top = mk(1.5, THREE.AdditiveBlending, 0.034);
    const GROW = 0.26, COLLAPSE = 0.68; // of 950ms: ~250ms in · ~400ms hold · ~300ms out
    this.tween(950, (p) => p, (p) => {
      const sIn = p < GROW ? easeOutQuint(p / GROW) : 1;
      const sOut = p > COLLAPSE ? 1 - easeInOutExpo((p - COLLAPSE) / (1 - COLLAPSE)) : 1;
      const s = Math.max(0.01, sIn * sOut);
      base.mesh.scale.setScalar(s);
      top.mesh.scale.setScalar(s);
      base.mesh.rotation.z = p * 2.4;   // counter-rotation is what sells the "mechanism opening"
      top.mesh.rotation.z = -p * 3.4;
      const a = Math.min(1, p * 5) * sOut;
      base.mat.opacity = a * (type === "xyz" ? 0.92 : 0.85);
      top.mat.opacity = a * 0.7;
    }, () => {
      this.fxGroup.remove(base.mesh, top.mesh);
      base.mesh.geometry.dispose(); base.mat.dispose();
      top.mesh.geometry.dispose(); top.mat.dispose();
    });
  }

  private fxActivate(m: Extract<DuelMotion, { kind: "activate" }>): void {
    const s = this.slotForZone(m.controller, m.location, m.sequence);
    const [x, z] = this.zoneWorld(m.controller, m.location, m.sequence);
    // `reveal` is store-derived truth (the card WAS face-down to the viewer) — take the zone's FX claim
    // over a recent arrival flight's window; the flip consumes the parked card back it landed.
    if (!this.reduced && m.reveal && this.claimZoneFx(m.controller, m.location, m.sequence, 1100, true)) {
      // The card was face-DOWN — a Set trap / Quick-Play turning up to activate. The MD beat: it rises
      // off the mat toward the viewer, does ONE smooth HORIZONTAL flip (about its own vertical axis)
      // revealing the real face big and readable, then settles back down flat, face-up.
      this.revealFlip(m);
      return;
    }
    if (s && s.occupied && !this.reduced) {
      // Already face-up (an ignition / continuous / monster effect firing). No flip — a surge: it
      // lifts and swells in place, so the activation reads without falsely "revealing".
      this.tween(
        420,
        easeOutQuint,
        (p) => { const k = Math.sin(p * Math.PI); s.group.position.y = 0.02 + k * 0.24; s.group.scale.setScalar(1 + k * 0.07); },
        () => { s.group.position.y = 0.02; s.group.scale.setScalar(1); },
      );
    }
    this.summonFlash(x, z, MAGENTA, 0.8);
  }

  /** The Set-card reveal: rise toward the camera (back to the viewer), one clean 180° horizontal flip
   *  to show the face, hold the beat, settle back flat face-up into the activation bloom. */
  private revealFlip(m: Extract<DuelMotion, { kind: "activate" }>): void {
    const [x, z] = this.zoneWorld(m.controller, m.location, m.sequence);
    const mine = m.controller === this.seat;
    const s = this.slotForZone(m.controller, m.location, m.sequence);
    const rz0 = s && s.occupied ? s.art.rotation.z : this.poseRz(mine, false); // set S/T sit portrait, owner-facing
    this.suppressZone(m.controller, m.location, m.sequence);
    const back = mine ? this.myBackTex : this.oppBackTex;
    const face = m.code ? this.artTexture(m.code) : back;
    const { grp } = this.makeFlightCard(face, back);
    const yFlip = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
    const q0 = this.flatQ(rz0, false);
    const camQ = this.camera.quaternion.clone();
    const qBack = camQ.clone().multiply(yFlip); // held up to the viewer, back showing
    const qFace = camQ;                          // ← the 180° horizontal flip lands here
    const q3 = this.flatQ(this.poseRz(mine, false), true); // settles flat in its owner-facing pose
    const base = new THREE.Vector3(x, 0.02, z);
    // rise mostly STRAIGHT UP off the zone (a slight lean toward the camera): the card stays visually
    // anchored to where it was set, just presented big and readable
    const camDir = this.camera.position.clone().sub(base).normalize();
    const apex = base.clone().add(new THREE.Vector3(camDir.x * 0.5, 1.7, camDir.z * 0.5));
    grp.position.copy(base); grp.quaternion.copy(q0);
    this.tween(920, (p) => p, (p) => {
      if (p < 0.3) {
        const k = easeOutQuint(p / 0.3);
        grp.position.lerpVectors(base, apex, k);
        grp.quaternion.slerpQuaternions(q0, qBack, k);
        grp.scale.setScalar(1 + 0.22 * k);
      } else if (p < 0.62) {
        const k = easeInOutExpo((p - 0.3) / 0.32);
        grp.quaternion.slerpQuaternions(qBack, qFace, k);
      } else {
        const k = easeInOutExpo((p - 0.62) / 0.38);
        grp.position.lerpVectors(apex, base, k);
        grp.quaternion.slerpQuaternions(qFace, q3, k);
        grp.scale.setScalar(1.22 - 0.22 * k);
      }
    }, () => {
      this.landFlight(grp, m.controller, m.location, m.sequence);
      this.summonFlash(x, z, MAGENTA, 0.9);
    });
  }

  private fxDestroy(m: Extract<DuelMotion, { kind: "destroy" }>): void {
    // The card DE-REZZES (dissolves to data, streaming to the graveyard) in the DOM motion layer — here
    // we play the in-place impact: hide the slot card (so the DOM dissolve reads as it breaking up), a
    // magenta "data-corruption" flash + electric-cyan sparks (cyberspace deletes, it doesn't burn), and
    // a screen shake scaled by whether it was a battle.
    const s = this.slotForZone(m.controller, m.location, m.sequence);
    const [x, z] = this.zoneWorld(m.controller, m.location, m.sequence);
    if (s) { s.exiting = true; s.group.visible = false; s.occupied = false; s.sig = ""; this.tweenDelay(700, () => { if (s) s.exiting = false; }); }
    this.hitstop(80); // §3.2: destruction gets the same freeze-then-release weight as a battle impact
    this.flash(x, z, m.battle ? new THREE.Color(0xff5e9e) : MAGENTA, 1.15);
    this.sparks(x, 0.1, z, new THREE.Color(0x7fe9ff), 14);
    this.shake = Math.min(1, this.shake + (m.battle ? 0.9 : 0.55));
  }

  /** A card relocates (send-to-GY, banish, discard, mill, bounce, return-to-deck): it flies there
   *  in-scene, through real 3D space — shrinking onto a pile, or arcing up into the hand fan. */
  private fxTravel(m: Extract<DuelMotion, { kind: "travel" }>): void {
    if (m.overlay) { this.fxOverlay(m); return; } // Xyz material attach/detach — never a placement
    const FIELD = LOCATION.MZONE | LOCATION.SZONE | LOCATION.FZONE;
    const s = this.slotForZone(m.from.controller, m.from.location, m.from.sequence);
    const [fx2, fz2] = this.zoneWorld(m.from.controller, m.from.location, m.from.sequence);
    const clearSource = () => {
      if (s && s.occupied) { s.exiting = true; s.group.visible = false; s.occupied = false; s.sig = ""; this.tweenDelay(700, () => { s.exiting = false; }); }
    };
    if (this.reduced) { clearSource(); return; }
    const mine = m.from.controller === this.seat;
    const back = mine ? this.myBackTex : this.oppBackTex;
    const face = m.code && !m.faceDown ? this.artTexture(m.code) : back;
    // source pose
    let from: THREE.Vector3, fromQ: THREE.Quaternion, fromS = 1, release = () => {};
    if (m.from.location & LOCATION.HAND) {
      const h = this.handSource(m.from.controller, m.code);
      from = h.pos; fromQ = h.q; fromS = h.s; release = h.release;
    } else if (m.from.location & FIELD) {
      from = new THREE.Vector3(fx2, 0.05, fz2);
      fromQ = this.flatQ(s?.occupied ? s.art.rotation.z : this.poseRz(mine, false), s?.occupied ? !s.sig.startsWith("0|back") : !m.faceDown);
      clearSource();
      this.sparks(fx2, 0.2, fz2, new THREE.Color(0x8fd0ff), 6);
    } else {
      from = new THREE.Vector3(fx2, 0.3, fz2);
      fromQ = this.flatQ(this.poseRz(mine, false), (m.from.location & (LOCATION.GRAVE | LOCATION.REMOVED)) !== 0);
    }
    // destination pose
    if (m.to.location & FIELD) {
      // PLACEMENT: the card flies onto its zone (the summon/set/activate event that follows adds the
      // bloom). Claim the zone so no other narration of the same act re-animates it.
      if (!this.claimZoneFx(m.to.controller, m.to.location, m.to.sequence, 900)) { release(); return; }
      const tm = m.to.controller === this.seat;
      const faceUp = !m.faceDown;
      // The landscape (defense) turn belongs ONLY to a monster set/summoned in defense. A set
      // spell/trap's face-down position ALSO carries the defense bit, so gate on the MONSTER zone —
      // otherwise every set S/T flies in sideways then snaps upright when the slot dresses it.
      const inMonsterZone = (m.to.location & LOCATION.MZONE) !== 0;
      const rz = this.poseRz(tm, inMonsterZone && !!m.defense);
      const [tx2, tz2] = this.zoneWorld(m.to.controller, m.to.location, m.to.sequence);
      this.suppressZone(m.to.controller, m.to.location, m.to.sequence);
      this.flyCard({
        face: m.code ? this.artTexture(m.code) : back, back, // two-sided: a Set turns over mid-air
        from, fromQ, fromS,
        to: new THREE.Vector3(tx2, 0.02, tz2), toQ: this.flatQ(rz, faceUp),
        // a peeked masked play REVEALS in the hand (flip in place) then flies face-up, a touch quicker
        // so the whole beat still lands with the summon bloom
        preFlip: m.revealAtHand ? 200 : undefined,
        arc: 1.1, dur: m.revealAtHand ? DUR.travel - 60 : DUR.travel,
        onLand: (grp) => { release(); this.landFlight(grp, m.to.controller, m.to.location, m.to.sequence); },
      });
      return;
    }
    if (m.to.location & LOCATION.HAND) {
      // bounce to a hand: arc up into the fan, landing exactly where the card will sit. The target is
      // a THUNK — it tracks the camera-anchored fan even while the camera moves.
      const tm = m.to.controller === this.seat;
      const n0 = this.lastHand[tm ? "me" : "opp"];
      const slotOf = () => {
        const n = Math.max(this.lastHand[tm ? "me" : "opp"], n0 + 1); // fan size incl. the arriving card
        return this.handCardTransform(n - 1, n, tm); // the bounced card takes the END slot
      };
      if (tm && m.code) this.handHide.mine.set(m.code, (this.handHide.mine.get(m.code) ?? 0) + 1);
      else if (!tm) this.handHide.opp++;
      this.flyCard({
        face: tm && m.code ? this.artTexture(m.code) : face, back,
        from, fromQ, fromS,
        to: () => this.camera.localToWorld(slotOf().pos.clone()),
        toQ: () => this.camera.quaternion.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), slotOf().roll)),
        toS: (tm ? HAND_H : OPP_HAND_H) / (CH * 0.95),
        arc: 1.2, dur: DUR.travel + 160, removeOnLand: true,
        onLand: () => {
          release();
          if (tm && m.code) this.handHide.mine.set(m.code, Math.max(0, (this.handHide.mine.get(m.code) ?? 1) - 1));
          else if (!tm) this.handHide.opp = Math.max(0, this.handHide.opp - 1);
          this.refreshBoard(); // reveals the real fan card the same tick the flight mesh goes
        },
      });
      return;
    }
    // into a pile (GY / banish / deck / extra): settle onto the stack and be swallowed
    const [tx2, tz2] = this.zoneWorld(m.to.controller, m.to.location, m.to.sequence);
    const tMine = m.to.controller === this.seat;
    const pileFaceUp = (m.to.location & (LOCATION.GRAVE | LOCATION.REMOVED)) !== 0 && !m.faceDown;
    this.flyCard({
      face, back, from, fromQ, fromS,
      to: new THREE.Vector3(tx2, 0.28, tz2), toQ: this.flatQ(this.poseRz(tMine, false), pileFaceUp), toS: 0.92,
      arc: 1.0, dur: DUR.travel + 80, fadeOut: true,
      onLand: () => release(),
    });
  }

  /** Xyz material attach/detach (m.overlay — locations pre-normalized to the HOST zone by the store):
   *  the material shrinks to a small card that tucks UNDER the host / slides out from beneath it on
   *  its way to the pile. Deliberately NOT a placement: no zone suppression, no park, no claim — the
   *  host card itself never blinks. */
  private fxOverlay(m: Extract<DuelMotion, { kind: "travel" }>): void {
    if (this.reduced) return;
    const FIELD = LOCATION.MZONE | LOCATION.SZONE | LOCATION.FZONE;
    const attach = (m.to.location & FIELD) !== 0;
    const host = attach ? m.to : m.from;
    const other = attach ? m.from : m.to;
    const [hx, hz] = this.zoneWorld(host.controller, host.location, host.sequence);
    const [ox, oz] = this.zoneWorld(other.controller, other.location, other.sequence);
    const mine = m.from.controller === this.seat;
    const back = mine ? this.myBackTex : this.oppBackTex;
    const face = m.code && !m.faceDown ? this.artTexture(m.code) : back;
    const under = new THREE.Vector3(hx, 0.012, hz);              // just beneath the host card's y (0.02)
    const underQ = this.flatQ(this.poseRz(host.controller === this.seat, false), true);
    const UNDER_S = 0.55;                                        // materials read smaller than the host
    const otherMine = other.controller === this.seat;
    const pilePose = this.flatQ(this.poseRz(otherMine, false), (other.location & (LOCATION.GRAVE | LOCATION.REMOVED)) !== 0 && !m.faceDown);
    if (attach) {
      this.flyCard({
        face, back,
        from: new THREE.Vector3(ox, 0.3, oz), fromQ: pilePose,
        to: under, toQ: underQ, toS: UNDER_S,
        arc: 0.8, dur: DUR.travel, fadeOut: true, // fades away as it tucks under; removed on land
      });
    } else {
      this.flyCard({
        face, back,
        from: under, fromQ: underQ, fromS: UNDER_S,
        to: new THREE.Vector3(ox, 0.28, oz), toQ: pilePose, toS: 0.92, // grows back to card size en route
        arc: 0.9, dur: DUR.travel, fadeOut: true,
      });
    }
  }

  /** Draws: each card peels off the deck and ARCS up into the hand fan, landing exactly where it will
   *  sit once the fan re-centres. Your own draws turn face-up toward you mid-flight (the MD "look at
   *  the card" beat); the opponent's stay hidden. */
  private fxDraw(m: Extract<DuelMotion, { kind: "draw" }>): void {
    if (this.reduced) return;
    const mine = m.controller === this.seat;
    // Turn-draw SHOWCASE (§3.3): your single-card draw pauses face-to-camera near screen centre for a
    // beat before joining the fan — the MD "look at the card" moment. Multi-draws keep the quick arcs.
    if (mine && m.cards.length === 1 && (m.cards[0] ?? 0) > 0) { this.drawShowcase(m.cards[0]!); return; }
    const [dx2, dz2] = slot(X_RAIL, Z_DECK, mine); // draws lift off the deck
    const back = mine ? this.myBackTex : this.oppBackTex;
    const n0 = this.lastHand[mine ? "me" : "opp"];
    const total = n0 + m.cards.length;
    for (let i = 0; i < m.cards.length; i++) {
      const code = m.cards[i] ?? 0;
      // the fan slot is a THUNK: it tracks the camera (duel-start dolly, shake) AND the fan re-centre
      // when the snapshot lands mid-flight — the card always arrives exactly on its final slot
      const slotOf = () => {
        const n = Math.max(this.lastHand[mine ? "me" : "opp"], total);
        return this.handCardTransform(n - m.cards.length + i, n, mine);
      };
      if (mine && code) this.handHide.mine.set(code, (this.handHide.mine.get(code) ?? 0) + 1);
      else if (!mine) this.handHide.opp++;
      this.flyCard({
        face: code ? this.artTexture(code) : back, back,
        from: new THREE.Vector3(dx2, 0.42, dz2), fromQ: this.flatQ(this.poseRz(mine, false), false),
        to: () => this.camera.localToWorld(slotOf().pos.clone()),
        toQ: () => this.camera.quaternion.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), slotOf().roll)),
        toS: (mine ? HAND_H : OPP_HAND_H) / (CH * 0.95),
        arc: mine ? 1.5 : 0.9, dur: DUR.travel + 260, delay: i * 130, removeOnLand: true,
        onLand: () => {
          if (mine && code) this.handHide.mine.set(code, Math.max(0, (this.handHide.mine.get(code) ?? 1) - 1));
          else if (!mine) this.handHide.opp = Math.max(0, this.handHide.opp - 1);
          this.refreshBoard(); // reveals the real fan card the same tick the flight mesh goes
        },
      });
    }
  }

  /** The turn-draw showcase: peel off the deck → hold face-to-camera near screen centre (~1.3× hand
   *  size, 320ms) → continue into the fan slot. Three legs on ONE visual: the hold leg re-pins the
   *  card to the camera each frame (the fan is camera-anchored, so the presentation pose must be too —
   *  a world-frozen card would drift during a camera beat). */
  private drawShowcase(code: number): void {
    const [dx2, dz2] = slot(X_RAIL, Z_DECK, true);
    const total = this.lastHand.me + 1;
    const slotOf = () => {
      const n = Math.max(this.lastHand.me, total); // fan size incl. the arriving card
      return this.handCardTransform(n - 1, n, true); // the drawn card takes the END slot
    };
    this.handHide.mine.set(code, (this.handHide.mine.get(code) ?? 0) + 1);
    const midLocal = new THREE.Vector3(0, -0.55, -HAND_DIST); // just below screen centre
    const midS = (HAND_H * 1.3) / (CH * 0.95);
    const face = this.artTexture(code);
    this.flyCard({
      face, back: this.myBackTex,
      from: new THREE.Vector3(dx2, 0.42, dz2), fromQ: this.flatQ(this.poseRz(true, false), false),
      to: () => this.camera.localToWorld(midLocal.clone()), toQ: () => this.camera.quaternion.clone(), toS: midS,
      arc: 1.3, dur: 430,
      onLand: (grp) => {
        // the HOLD: pinned to the camera for the beat
        this.tween(320, (p) => p, () => {
          grp.position.copy(this.camera.localToWorld(midLocal.clone()));
          grp.quaternion.copy(this.camera.quaternion);
        }, () => {
          // leg 3: from wherever the hold left it, into the fan slot (thunk targets track the re-fan)
          const from = grp.position.clone(), fromQ = grp.quaternion.clone();
          this.removeFlight(grp);
          this.flyCard({
            face, back: this.myBackTex,
            from, fromQ, fromS: midS,
            to: () => this.camera.localToWorld(slotOf().pos.clone()),
            toQ: () => this.camera.quaternion.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), slotOf().roll)),
            toS: HAND_H / (CH * 0.95),
            arc: 0.25, dur: 300, removeOnLand: true,
            onLand: () => {
              this.handHide.mine.set(code, Math.max(0, (this.handHide.mine.get(code) ?? 1) - 1));
              this.refreshBoard(); // reveals the real fan card the same tick the flight mesh goes
            },
          });
        });
      },
    });
  }

  /** Position change. A face change is a REAL card flip — the smooth horizontal turn-over in place.
   *  A pure ATK ⇄ DEF switch is a clean quarter-turn on the table (no false flip). */
  private fxFlip(m: Extract<DuelMotion, { kind: "flip" }>): void {
    const s = this.slotForZone(m.controller, m.location, m.sequence);
    if (!s || !s.occupied || this.reduced) return;
    const wasDown = (m.prev & POS.FACEDOWN) !== 0;
    const isDown = (m.now & POS.FACEDOWN) !== 0;
    // The defense bit only MEANS defense in the MONSTER zone: OCGcore reports spell/trap positions
    // with BOTH battle bits set (face-up S/T = 0x5 = FACEUP_ATTACK|FACEUP_DEFENSE), so an un-gated
    // check turned an activating set spell landscape mid-flip before the snapshot corrected it.
    const defNow = (m.location & LOCATION.MZONE) !== 0 && (m.now & POS.DEFENSE) !== 0;
    if (wasDown !== isDown) {
      this.flipOver(m.controller, m.location, m.sequence, m.code, !isDown, defNow);
      return;
    }
    const rz0 = s.art.rotation.z;
    const rzTo = this.poseRz(m.controller === this.seat, defNow); // owner-facing, matches the next dress
    const gz0 = s.glow.rotation.z, gzTo = defNow ? Math.PI / 2 : 0; // glow rect is symmetric under 180°
    this.tween(DUR.base + 80, easeOutQuint, (p) => {
      s.group.position.y = 0.02 + Math.sin(p * Math.PI) * 0.22;
      s.art.rotation.z = rz0 + (rzTo - rz0) * p;
      s.foil.rotation.z = s.art.rotation.z;
      s.glow.rotation.z = gz0 + (gzTo - gz0) * p;
    }, () => { s.group.position.y = 0.02; s.sig = ""; this.refreshBoard(); });
  }

  /** Flip a card over IN PLACE (face-down ⇄ face-up): it lifts, rolls smoothly about its own axis —
   *  the horizontal flip of turning a real card — and settles. Flip summons, effect flips, Book of Moon.
   *  `force` = the caller KNOWS this narration owns the theatre (a store-derived reveal) — it takes the
   *  zone's FX claim over even if a recent flight already held it. */
  private flipOver(controller: number, location: number, sequence: number, code: number, toFaceUp: boolean, defense: boolean, force = false): void {
    if (this.reduced) return;
    // one narration only: a flip summon is often ALSO reported as a posChange — animate once
    if (!this.claimZoneFx(controller, location, sequence, 700, force)) return;
    defense = defense && (location & LOCATION.MZONE) !== 0; // landscape is a MONSTER-zone pose, never S/T
    const [x, z] = this.zoneWorld(controller, location, sequence);
    const mine = controller === this.seat;
    const s = this.slotForZone(controller, location, sequence);
    // fallback start pose (slot suppressed/parked): a face-down monster lies landscape, so flipping UP
    // starts horizontal; flipping DOWN (Book of Moon) starts from the usual vertical attack pose.
    const rz0 = s && s.occupied ? s.art.rotation.z : this.poseRz(mine, (location & LOCATION.MZONE) !== 0 && toFaceUp);
    const q0 = this.flatQ(rz0, !toFaceUp);
    const rz1 = this.poseRz(mine, defense);
    const q1 = this.flatQ(rz1, toFaceUp);
    this.suppressZone(controller, location, sequence);
    const back = mine ? this.myBackTex : this.oppBackTex;
    const { grp } = this.makeFlightCard(code ? this.artTexture(code) : back, back);
    grp.position.set(x, 0.02, z); grp.quaternion.copy(q0);
    this.tween(480, (p) => p, (p) => {
      grp.position.y = 0.02 + Math.sin(p * Math.PI) * 0.55;
      grp.quaternion.slerpQuaternions(q0, q1, easeInOutExpo(p));
    }, () => this.landFlight(grp, controller, location, sequence));
  }

  private fxAttack(m: Extract<DuelMotion, { kind: "attack" }>): void {
    const s = this.slotForZone(m.attacker.controller, m.attacker.location, m.attacker.sequence);
    const ts = m.target ? this.slotForZone(m.target.controller, m.target.location, m.target.sequence) : undefined;
    const [ax, az] = this.zoneWorld(m.attacker.controller, m.attacker.location, m.attacker.sequence);
    const [tx, tz] = m.target ? this.zoneWorld(m.target.controller, m.target.location, m.target.sequence) : [ax * 0.15, -az * 0.55];
    const mine = m.attacker.controller === this.seat;
    // a curved beam from attacker → target shows who is striking whom.
    this.attackLine(ax, az, tx, tz, mine ? CYAN : MAGENTA);
    if (s && s.occupied && !this.reduced) {
      // The held declaration becomes a physical blow: pull 16% BACK, hold the tension, then take a
      // long accelerating run into the defender before recoiling home. The anticipation makes the
      // strike legible instead of reading like a card simply sliding to another zone.
      const g = s.group;
      const dx = tx - ax, dz = tz - az;
      this.tween(ATTACK_TIMING.total, (p) => p, (p) => {
        const pullEnd = ATTACK_TIMING.pullback / ATTACK_TIMING.total;
        const windupEnd = (ATTACK_TIMING.pullback + ATTACK_TIMING.windup) / ATTACK_TIMING.total;
        const impactAt = ATTACK_TIMING.impact / ATTACK_TIMING.total;
        const reach = p < pullEnd
          ? -easeOutQuint(p / pullEnd) * 0.16
          : p < windupEnd
            ? -0.16
            : p < impactAt
              ? -0.16 + easeInCubic((p - windupEnd) / (impactAt - windupEnd)) * 0.92
              : 0.76 * (1 - easeOutQuint((p - impactAt) / (1 - impactAt)));
        g.position.set(s.x + dx * reach, 0.02 + Math.sin(Math.min(1, p * 2.2) * Math.PI) * 0.24, s.z + dz * reach);
      }, () => g.position.set(s.x, 0.02, s.z));
      // the camera leans INTO the exchange (§3.1): a subtle dip + drift toward the clash midpoint
      // while the lunge runs, easing back out as the recoil settles.
      const mid = new THREE.Vector3((ax + tx) / 2, 0, (az + tz) / 2);
      this.camBeat(ATTACK_TIMING.total + 30, (p, pos, look) => {
        const k = DuelArena.env(p, ATTACK_TIMING.impact / ATTACK_TIMING.total);
        pos.y -= 0.34 * k;
        look.lerp(mid, 0.1 * k);
      });
    }
    // Impact is held until the end of the pull-back + wind-up + accelerating run. The §3.2 order matters: the struck card flashes
    // WHITE and the whole scene freezes on that same frame (hitstop pauses the tween clock, so the
    // flash/sparks/recoil below all HOLD at p=0), then everything releases together — kick, shake,
    // sparks, recoil, and the DOM damage numbers slamming in.
    this.tweenDelay(ATTACK_TIMING.impact, () => {
      const n = Math.hypot(tx - ax, tz - az) || 1;
      this.impactFrame(tx, tz, ts);
      this.hitstop(80);
      this.camKick((tx - ax) / n, (tz - az) / n); // DIRECTIONAL: the camera reads the blow's direction
      this.flash(tx, tz, mine ? new THREE.Color(0xbff0ff) : new THREE.Color(0xffd0e0), 1.1);
      this.sparks(tx, 0.16, tz, mine ? new THREE.Color(0x9bf0ff) : new THREE.Color(0xff7ab0), 18);
      this.shake = Math.min(1, this.shake + 0.85);
      if (ts && ts.occupied && !this.reduced) {
        const tg = ts.group;
        const kx = ((tx - ax) / n) * 0.16, kz = ((tz - az) / n) * 0.16;
        this.tween(300, easeOutQuint, (q) => { const k = Math.sin(q * Math.PI); tg.position.set(ts.x + kx * k, 0.02 + 0.05 * k, ts.z + kz * k); }, () => tg.position.set(ts.x, 0.02, ts.z));
      }
    });
  }

  /** The ~2-frame white IMPACT FRAME on the struck card (§3.2): an additive quad over the defender's
   *  slot, 60ms on the tween clock — so the simultaneous hitstop HOLDS the white frame while the scene
   *  is frozen (the classic fighting-game read), then it fades on release. */
  private impactFrame(x: number, z: number, s?: SlotMesh): void {
    if (this.reduced) return;
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.92, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false });
    const q = new THREE.Mesh(new THREE.PlaneGeometry(CW * 1.02, CH * 1.02), mat);
    q.rotation.x = -Math.PI / 2;
    q.rotation.z = s?.occupied ? s.art.rotation.z : 0; // match a defense/opponent card's orientation
    q.position.set(x, (s?.group.position.y ?? 0.02) + 0.05, z);
    q.renderOrder = 30;
    this.fxGroup.add(q);
    this.tween(60, (p) => p, (p) => { mat.opacity = 0.92 * (1 - p); }, () => { this.fxGroup.remove(q); q.geometry.dispose(); mat.dispose(); });
  }

  // ── the supporting beats (animation-plan §3 / Phase C3) ─────────────────────────
  /** The duel-ending blow (§3.2): 300ms of slow-motion + a slow pull-back the camera HOLDS while the
   *  red vignette (DuelCinematics) closes in — MD's signature finish. The end screen takes over next. */
  private fxLethal(): void {
    if (this.reduced) return;
    this.slowLeft = 0.5; // 300ms at ×0.25 + 200ms easing back to ×1, consumed in REAL time by the loop
    const away = this.camRest.clone().sub(this.camLook).normalize();
    this.camBeat(1600, (p, pos) => {
      const k = DuelArena.env(p, 0.19, 0.53); // in ~300ms, HOLD the moment, release with the end screen
      pos.addScaledVector(away, 1.05 * k); // pull back…
      pos.y += 0.5 * k;                    // …and rise a touch — the slight tilt-down read
    });
  }

  /** SHUFFLE_HAND: the fan gathers to a stack at its centre and re-deals — the beat that tells the
   *  player their knowledge of that hand's ORDER is now void. Implemented as a 0→1→0 scalar the loop
   *  blends into every fan card's base target, so it survives fan rebuilds mid-animation. */
  private fxShuffleHand(m: Extract<DuelMotion, { kind: "shuffleHand" }>): void {
    if (this.reduced) return;
    const side = m.controller === this.seat ? "me" : "opp";
    this.tween(170, easeOutQuint, (p) => { this.handGather[side] = p; }, () => {
      this.tweenDelay(90, () => this.tween(280, easeOutQuint, (p) => { this.handGather[side] = 1 - p; }));
    });
  }

  /** SHUFFLE_DECK: a quick riffle wiggle on that player's deck pile. The piles rebuild per snapshot,
   *  so this transient offset on the pile GROUP can never leak — a rebuilt pile starts at identity. */
  private fxShuffleDeck(m: Extract<DuelMotion, { kind: "shuffleDeck" }>): void {
    if (this.reduced) return;
    const mine = m.controller === this.seat;
    const grp = this.pilesGroup.children.find((c) => {
      const p = c.userData.pile as { kind: PileKind; mine: boolean } | undefined;
      return p?.kind === "deck" && p.mine === mine;
    });
    if (!grp) return;
    this.tween(420, (p) => p, (p) => {
      const w = Math.sin(p * Math.PI * 5) * (1 - p); // 2-3 riffle offsets, dying out
      grp.position.x = w * 0.07;
      grp.rotation.y = w * 0.07;
    }, () => { grp.position.x = 0; grp.rotation.y = 0; });
  }

  /** SHUFFLE_SET_CARD — the three-card monte: the controller's face-down cards in that row visibly
   *  slide through each other's spots and back, so the player SEES their tracking break. The engine
   *  swaps the real occupants silently and the next snapshot re-dresses the slots, so ending back at
   *  identity is correct whichever permutation the core rolled. */
  private fxShuffleSet(m: Extract<DuelMotion, { kind: "shuffleSet" }>): void {
    if (this.reduced) return;
    const side = m.controller === this.seat ? "me" : "opp";
    const loc = (m.location & LOCATION.MZONE) !== 0 ? "m" : "s";
    const groups: SlotMesh[] = [];
    for (let i = 0; i < 5; i++) {
      const s = this.slots.get(slotKey(side, loc, i));
      if (s && s.occupied && !s.exiting && s.sig.startsWith("0|back")) groups.push(s);
    }
    if (groups.length < 2) return;
    const spots = groups.map((s) => ({ x: s.x, z: s.z }));
    this.tween(460, (p) => p, (p) => {
      // out to the NEXT card's spot (cyclic) and back, alternating lifts so the passing cards clear
      const k = p < 0.5 ? easeInOutExpo(p / 0.5) : 1 - easeInOutExpo((p - 0.5) / 0.5);
      for (let i = 0; i < groups.length; i++) {
        const s = groups[i]!, to = spots[(i + 1) % spots.length]!;
        s.group.position.x = s.x + (to.x - s.x) * k;
        s.group.position.z = s.z + (to.z - s.z) * k;
        s.group.position.y = 0.02 + Math.sin(p * Math.PI) * (0.05 + 0.05 * (i % 2));
      }
    }, () => { for (const s of groups) s.group.position.set(s.x, 0.02, s.z); });
  }

  /** EQUIP: a gold beam arcs from the equip card to its host + a gold ring blooms on the host — the
   *  attach must be SEEN. Gold is the reserved DP/equip accent, so the relationship is unmistakable.
   *  Same tube-geometry approach as the attack beam, thinner and softer. */
  private fxEquip(m: Extract<DuelMotion, { kind: "equip" }>): void {
    if (this.reduced) return;
    const [ex, ez] = this.zoneWorld(m.from.controller, m.from.location, m.from.sequence);
    const [hx, hz] = this.zoneWorld(m.to.controller, m.to.location, m.to.sequence);
    // Degenerate span is REACHABLE here: an equip narrated between two non-field locations collapses
    // both ends onto the off-table hand fallback [0, ±Z_HAND] (same point), and from === to is
    // expressible too. Same law as attackLine: skip the beam (nothing to show), keep the host ring.
    if (Math.hypot(hx - ex, hz - ez) >= 1e-3) {
      const start = new THREE.Vector3(ex, 0.12, ez);
      const end = new THREE.Vector3(hx, 0.12, hz);
      const lift = Math.hypot(hx - ex, hz - ez) * 0.3 + 0.5;
      const mid = new THREE.Vector3((ex + hx) / 2, 0.12 + lift, (ez + hz) / 2);
      const geo = new THREE.TubeGeometry(new THREE.QuadraticBezierCurve3(start, mid, end), 24, 0.028, 8, false);
      const mat = new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
      const tube = new THREE.Mesh(geo, mat);
      this.fxGroup.add(tube);
      this.tween(560, (p) => p, (p) => { mat.opacity = pulse(Math.min(1, p * 1.25)) * 0.75; }, () => { this.fxGroup.remove(tube); geo.dispose(); mat.dispose(); });
    }
    // the ring: blooms outward on the host and fades — "attached HERE"
    const rgeo = new THREE.RingGeometry(0.5, 0.56, 48);
    const rmat = new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
    const ring = new THREE.Mesh(rgeo, rmat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(hx, 0.06, hz);
    ring.renderOrder = 6;
    this.fxGroup.add(ring);
    this.tween(430, easeOutQuint, (p) => { ring.scale.setScalar(0.7 + 0.75 * p); rmat.opacity = 0.85 * (1 - p); }, () => { this.fxGroup.remove(ring); rgeo.dispose(); rmat.dispose(); });
  }

  /** Chain link N is RESOLVING (§3.4): one bright pulse on its levitating card — WHICH effect is
   *  firing right now reads at a glance. Field links pulse the slot group; pile links pulse the hover
   *  card riding their chain marker. */
  private fxResolve(m: Extract<DuelMotion, { kind: "resolve" }>): void {
    if (this.reduced) return;
    const s = this.slotForZone(m.controller, m.location, m.sequence);
    const [x, z] = this.zoneWorld(m.controller, m.location, m.sequence);
    const fx = this.chainFx.get(m.link);
    const target = s && s.occupied && !s.exiting ? s.group : fx?.grp ?? null;
    if (target) {
      const s0 = target.scale.x;
      this.tween(340, (p) => p, (p) => target.scale.setScalar(s0 * (1 + 0.09 * pulse(p))), () => target.scale.setScalar(s0));
    }
    // white surge quad at the card's (levitated) height — the flash half of the pulse
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false });
    const q = new THREE.Mesh(new THREE.PlaneGeometry(CW * 1.05, CH * 1.05), mat);
    q.rotation.x = -Math.PI / 2;
    q.rotation.z = s?.occupied ? s.art.rotation.z : 0;
    q.position.set(x, (target?.position.y ?? 0.02) + 0.06, z);
    q.renderOrder = 30;
    this.fxGroup.add(q);
    this.tween(300, (p) => p, (p) => { mat.opacity = pulse(p) * 0.6; }, () => { this.fxGroup.remove(q); q.geometry.dispose(); mat.dispose(); });
  }

  /** A curved additive beam from attacker zone to target zone, briefly flashed. */
  private attackLine(ax: number, az: number, tx: number, tz: number, color: THREE.Color): void {
    if (this.reduced) return;
    // Degenerate span — attacker and target resolving to the SAME spot is expressible in the motion
    // vocabulary (a self-target `attack`, replay/NPC narration quirks). The bezier's apex tangent is
    // zero there and TubeGeometry stays finite ONLY thanks to three's internal normalize() zero-guard
    // (verified: collapsed but finite). A zero-length beam shows nothing anyway — skip it instead of
    // leaning on that guard to keep degenerate geometry away from the GPU.
    if (Math.hypot(tx - ax, tz - az) < 1e-3) return;
    const start = new THREE.Vector3(ax, 0.12, az);
    const end = new THREE.Vector3(tx, 0.12, tz);
    const lift = Math.hypot(tx - ax, tz - az) * 0.28 + 0.55;
    const mid = new THREE.Vector3((ax + tx) / 2, 0.12 + lift, (az + tz) / 2);
    const curve = new THREE.QuadraticBezierCurve3(start, mid, end);
    const geo = new THREE.TubeGeometry(curve, 24, 0.045, 8, false);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
    const tube = new THREE.Mesh(geo, mat);
    this.fxGroup.add(tube);
    this.tween(500, (p) => p, (p) => { mat.opacity = Math.sin(Math.min(1, p * 1.4) * Math.PI) * 0.85; }, () => { this.fxGroup.remove(tube); geo.dispose(); mat.dispose(); });
  }

  // ── small FX primitives ──────────────────────────────────────────────────────────
  private flash(x: number, z: number, color: THREE.Color, strength: number): void {
    if (this.reduced) return;
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.0, blending: THREE.AdditiveBlending, depthWrite: false });
    const m = new THREE.Mesh(new THREE.PlaneGeometry(CW * 1.5, CH * 1.4), mat);
    m.rotation.x = -Math.PI / 2; m.position.set(x, 0.06, z);
    this.fxGroup.add(m);
    this.tween(300, easeOutQuint, (p) => { mat.opacity = Math.sin(Math.min(1, p) * Math.PI) * 0.6 * strength; m.scale.setScalar(0.8 + p * 0.6); }, () => { this.fxGroup.remove(m); m.geometry.dispose(); mat.dispose(); });
  }

  private sparks(x: number, y: number, z: number, color: THREE.Color, n: number): void {
    if (this.reduced) return;
    // WebGPU has no sized point primitive, so the burst is a handful of additive sprites sharing one
    // material (single opacity fade) and the soft radial texture the light-pools already use.
    const grp = new THREE.Group();
    const mat = new THREE.SpriteMaterial({ map: this.bloomTexture(), color, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false });
    const vel: number[] = [];
    for (let i = 0; i < n; i++) {
      const s = new THREE.Sprite(mat);
      s.scale.setScalar(0.11);
      grp.add(s);
      const a = Math.random() * Math.PI * 2, sp = 0.6 + Math.random() * 1.6;
      vel.push(Math.cos(a) * sp, 1 + Math.random() * 2, Math.sin(a) * sp);
    }
    grp.position.set(x, y, z);
    this.fxGroup.add(grp);
    const G = 4.5;
    this.tween(560, (p) => p, (p) => {
      const t = 0.56 * p;
      for (let i = 0; i < n; i++) grp.children[i]!.position.set(vel[i * 3]! * t, Math.max(0, vel[i * 3 + 1]! * t - 0.5 * G * t * t), vel[i * 3 + 2]! * t);
      mat.opacity = 0.9 * (1 - p);
    }, () => { this.fxGroup.remove(grp); mat.dispose(); });
  }

  // ── tween scheduler ────────────────────────────────────────────────────────────
  private tween(durMs: number, ease: (p: number) => number, step: (p: number) => void, done?: () => void): void {
    this.tweens.push({ t: 0, dur: durMs / 1000, ease, step, done });
  }
  private tweenDelay(ms: number, fn: () => void): void { this.tween(ms, (p) => p, () => {}, fn); }
  /** Cancel tweens that target a group (so a new pop-in doesn't fight a finishing tween). */
  private kill(_g: THREE.Object3D): void { /* slots use single-shot tweens; explicit cancel not needed yet */ }

  private advanceTweens(dt: number): void {
    // NaN hardening for the clock itself: a non-finite dt would push every tween's `t` to NaN, so
    // p = NaN forever (NaN ≥ 1 is false → the tween is KEPT and steps a NaN pose every frame) — the
    // whole board freezes mid-animation with JS perfectly alive. Refuse the poison at the gate.
    if (!Number.isFinite(dt)) dt = 0;
    this.fxT += dt; // the pausable FX clock — shake noise phase freezes with the hitstop / slows with slow-mo
    if (this.tweens.length) {
      const keep: Tween[] = [];
      for (const tw of this.tweens) {
        tw.t += dt;
        // dur ≤ 0 under a hitstop (dt = 0) is 0/0 = NaN — same stuck-forever failure; treat as done.
        const p = tw.dur > 0 ? Math.min(1, tw.t / tw.dur) : 1;
        // A throw inside ONE tween's step must never wedge the clock: unguarded, the exception
        // killed advanceTweens (and the loop's render behind it) EVERY frame — the whole arena
        // froze on its last frame while the duel kept playing in audio. Drop the broken tween,
        // keep the board alive; one glitchy beat beats a dead duel (same law as drainPlay).
        try {
          tw.step(tw.ease(p));
          if (p >= 1) tw.done?.(); else keep.push(tw);
        } catch (e) {
          console.error("[arena] tween step failed, dropping it to keep the board alive", e);
        }
      }
      this.tweens = keep;
    }
    // camera beats ride the SAME clock (so headless advanceTweens() stepping drives the camera too)
    if (this.camFx.length) {
      const keep: CamFx[] = [];
      for (const fx of this.camFx) { fx.t += dt; if (fx.t < fx.dur) keep.push(fx); }
      this.camFx = keep;
      this.camDirty = true;
    }
    try { this.applyCamera(dt); }
    catch (e) {
      // a broken camera beat must not freeze the scene — recover to the rest pose and drop all beats
      console.error("[arena] camera beat failed, resetting to rest", e);
      this.camFx = [];
      this.camera.position.copy(this.camRest);
    }
  }

  // ── interaction ─────────────────────────────────────────────────────────────────
  private pick(ev: PointerEvent | MouseEvent): { card: QueriedCard } | null {
    const r = this.canvas.getBoundingClientRect();
    this.pointer.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    // Hand cards sit in front of the board, so include them; intersectObjects sorts nearest-first, so a
    // hand card in front of a board slot wins naturally.
    const hits = this.raycaster.intersectObjects([...this.handGroup.children, ...this.slotsGroup.children], true);
    for (const h of hits) {
      const card = h.object.userData?.card as QueriedCard | undefined;
      if (card) return { card };
    }
    return null;
  }
  /** In manual-placement mode, raycast the lit zones instead of cards. */
  private pickZone(ev: MouseEvent): PlaceTarget | null {
    if (!this.placeActive || this.zoneGroup.children.length === 0) return null;
    const r = this.canvas.getBoundingClientRect();
    this.pointer.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.zoneGroup.children, false);
    return (hits[0]?.object.userData?.zone as PlaceTarget | undefined) ?? null;
  }
  /** Raycast the deck / gy / extra / banished stacks. ONLY the dedicated invisible hit planes are
   *  click targets (each sized to its own pile), and the NEAREST one wins, so the pile physically
   *  under the cursor is what's picked, true 3D-object hit-testing. No kind-based preference: the deck,
   *  being closest to the camera, is reliably clickable on its own footprint (it was previously losing
   *  overlapping clicks to a deprioritised view pile, so surrender never fired). */
  private pickPile(ev: MouseEvent): { kind: PileKind; mine: boolean } | null {
    if (this.pilesGroup.children.length === 0) return null;
    const r = this.canvas.getBoundingClientRect();
    this.pointer.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    // intersectObjects returns hits sorted nearest-first → the first hit plane is the frontmost pile.
    const hits = this.raycaster.intersectObjects(this.pilesGroup.children, true);
    for (const h of hits) {
      if (h.object.userData?.pileHit !== true) continue; // only the canonical hit planes are clickable
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData?.pile) return o.userData.pile as { kind: PileKind; mine: boolean }; o = o.parent; }
    }
    return null;
  }
  private onPointerMove = (e: PointerEvent) => {
    if (this.placeActive) { this.canvas.style.cursor = this.pickZone(e) ? "pointer" : "default"; return; }
    const hit = this.pick(e);
    this.canvas.style.cursor = hit?.card || this.pickPile(e) ? "pointer" : "default";
    this.cb.onHover(hit?.card ?? null);
    const nextKey = hit?.card ? `${hit.card.controller}:${hit.card.location}:${hit.card.sequence}` : null;
    // little tick when the cursor first lands on a hand card (board hovers stay silent)
    if (nextKey && nextKey !== this.hoverKey && hit!.card!.location === LOCATION.HAND) uiHover();
    this.hoverKey = nextKey;
    // attack-aim arc: snap to a hovered legal target, else hide.
    if (this.aimFrom) {
      const c = hit?.card;
      if (c && this.aimTargetKeys.has(`${c.controller}:${c.location}:${c.sequence}`)) this.drawAim(c);
      else this.clearAimLine();
    }
  };
  private onPointerLeave = () => { this.cb.onHover(null); this.hoverKey = null; if (this.aimFrom) this.clearAimLine(); };
  private onClick = (e: MouseEvent) => {
    if (this.placeActive) {
      const z = this.pickZone(e);
      if (z) this.cb.onZoneClick?.(z.controller, z.location, z.sequence);
      return;
    }
    const hit = this.pick(e);
    if (hit) { this.cb.onClick(hit.card, e.clientX, e.clientY); return; }
    const pile = this.pickPile(e);
    if (pile) { this.cb.onPileClick?.(pile.kind, pile.mine); return; }
    this.cb.onBackdrop?.(); // clicked empty board → dismiss the card-detail panel
  };

  // ── loop / resize / dispose ────────────────────────────────────────────────────
  private render(): void {
    if (!this.ready) return;
    // A poisoned render pass (a texture the GPU rejected, a driver hiccup) must not freeze the board
    // on its last frame forever — log (throttled) and keep the loop alive; passes usually recover
    // once the offending resource is swapped (e.g. the missing-art fallback lands).
    try {
      this.renderer.render(this.scene, this.camera);
      this.renderFails = 0; // a clean pass resets the strike count
    } catch (e) {
      const now = performance.now();
      if (now - this.lastRenderErr > 2000) { this.lastRenderErr = now; console.error("[arena] render pass failed (continuing)", e); }
      // Repeated failures = the pass is poisoned and the player is staring at a frozen frame while
      // the duel plays on in audio. Catching the throw keeps JS alive but NOT the picture — after a
      // few consecutive strikes, hand the duel to the 2D board (same escape hatch as a failed init).
      if (++this.renderFails >= 30 && !this.fellBack) { // ~0.5–1s of dead frames
        console.error("[arena] render pass failing repeatedly — handing the duel to the 2D board");
        this.failOver();
      }
    }
  }
  private lastRenderErr = 0;
  private renderFails = 0;
  private fellBack = false; // the 2D-board fallback fired — every escape hatch funnels through failOver ONCE

  /** Hand the duel to the 2D board, exactly once. Every post-init escape hatch (render-fail streak,
   *  device.lost, the GPU presentation watchdog) funnels through here so they can never double-fire. */
  private failOver(): void {
    if (this.fellBack || this.disposed) return;
    this.fellBack = true;
    this.cb.onInitFail?.();
  }

  // ── GPU presentation watchdog ──────────────────────────────────────────────────
  // The third prod freeze proved presentation can die with NO JS throw, NO device.lost and render()
  // "succeeding" from JS's point of view: the queue silently stops completing work while the duel
  // plays on in audio — both older tripwires stay silent. So the loop keeps a heartbeat in flight:
  // every WD_EVERY frames it asks the GPU queue for an ACK (onSubmittedWorkDone) and if that ACK
  // lags WD_LIMIT loop FRAMES (frames, not wall time — a hidden tab pauses rAF, frames stop
  // accumulating, and the watchdog must never false-positive there) presentation is declared dead.
  // Recovery ladder: (1) re-kick the canvas context once (resize() → setSize reconfigures the
  // swapchain — recovers a wedged surface without losing the duel), (2) if the next heartbeat dies
  // too, hand the duel to the 2D board.
  private wdFrame = 0;        // loop frames while ready (rAF-driven, pauses with the tab)
  private wdBeatFrame = -1;   // frame the in-flight heartbeat was enqueued at (-1 = none in flight)
  private wdSeq = 0;          // heartbeat identity — a stale ACK (post-resize / post-timeout) is ignored
  private wdRekicked = false; // ladder stage 1 (context re-kick) already fired for the current outage
  private wdOff = false;      // queue API unreachable on this backend → watchdog off, never guess
  private static readonly WD_EVERY = 45;  // heartbeat cadence (loop frames)
  private static readonly WD_LIMIT = 150; // ACK budget: ~2.5s at 60fps (5s at the 30fps cap — deliberately lax)

  private watchdog(): void {
    if (!this.ready || this.disposed || this.wdOff || this.fellBack) return;
    this.wdFrame++;
    if (this.wdBeatFrame >= 0) {
      // one heartbeat in flight at a time — check its frame budget
      if (this.wdFrame - this.wdBeatFrame < DuelArena.WD_LIMIT) return;
      this.wdSeq++; this.wdBeatFrame = -1; // declare it dead; a late ACK must not resurrect old state
      if (!this.wdRekicked) {
        this.wdRekicked = true;
        console.error(`[arena] GPU heartbeat silent for ${DuelArena.WD_LIMIT} frames while the loop kept ticking — re-kicking the canvas context (fails=${this.renderFails}, tweens=${this.tweens.length})`);
        try { this.resize(); } catch (e) { console.error("[arena] context re-kick failed", e); }
      } else {
        console.error("[arena] GPU heartbeat silent again after the re-kick — presentation is dead, handing the duel to the 2D board");
        this.failOver();
      }
      return;
    }
    if (this.wdFrame % DuelArena.WD_EVERY !== 0) return;
    let ack: Promise<undefined> | undefined;
    try {
      const q = (this.renderer as unknown as { backend?: { device?: { queue?: { onSubmittedWorkDone?: () => Promise<undefined> } } } }).backend?.device?.queue;
      ack = q?.onSubmittedWorkDone?.();
    } catch { /* backend shape differs — fall through to disable */ }
    if (!ack) { this.wdOff = true; return; } // no queue API here → skip the watchdog rather than false-positive
    const seq = ++this.wdSeq;
    this.wdBeatFrame = this.wdFrame;
    ack.then(
      () => { if (!this.disposed && seq === this.wdSeq) { this.wdBeatFrame = -1; this.wdRekicked = false; } }, // healthy ACK re-arms the ladder
      () => { if (!this.disposed && seq === this.wdSeq) this.wdBeatFrame = -1; }, // a REJECTED ACK = device loss; the device.lost hook owns that fallback
    );
  }

  // ── DEV NaN tripwire ───────────────────────────────────────────────────────────
  // A NaN can reach the GPU without ANY JS throw (a poisoned matrix silently kills draws — or the
  // whole pass). In dev, sweep the scene every ~90 frames and NAME the offender the moment one
  // exists, so fuzz / mock / dev-play sessions surface the producing beat instantly.
  private nanFrame = 0;
  private lastNanErr = 0;
  private nanScan(): void {
    if (++this.nanFrame % 90 !== 0) return;
    const offenders: string[] = [];
    const path = (o: THREE.Object3D): string => {
      const parts: string[] = [];
      for (let c: THREE.Object3D | null = o; c; c = c.parent) parts.unshift(c.name || c.type);
      return parts.join(">");
    };
    this.scene.traverse((o) => {
      if (offenders.length >= 4) return;
      const e = o.matrixWorld.elements;
      for (let i = 0; i < 16; i++) if (!Number.isFinite(e[i]!)) { offenders.push(`matrixWorld @ ${path(o)}`); return; }
      const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      if (m && !Array.isArray(m) && !Number.isFinite(m.opacity)) offenders.push(`material.opacity @ ${path(o)}`);
    });
    const pm = this.camera.projectionMatrix.elements, vm = this.camera.matrixWorldInverse.elements;
    for (let i = 0; i < 16; i++) if (!Number.isFinite(pm[i]!) || !Number.isFinite(vm[i]!)) { offenders.push("camera matrices"); break; }
    if (offenders.length && performance.now() - this.lastNanErr > 2000) {
      this.lastNanErr = performance.now();
      console.error("[arena] NaN tripwire — non-finite value headed for the GPU:", offenders.join(" · "));
    }
  }
  private loop = () => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    // A NATIVE rAF tick arrived — if the starvation fallback was driving, rAF takes the loop back.
    // The flag flip is all it takes: the 33ms fallback interval sees it and clears itself, so the
    // two drivers can never run the board together beyond this single handover frame.
    if (this.fallbackDriving) {
      this.fallbackDriving = false;
      console.error("[arena] rAF recovered — handing the board back to requestAnimationFrame");
    }
    this.tick();
  };

  /** ONE frame of the board: clocks → tweens → camera → dressing → render throttle → GPU heartbeat.
   *  Deliberately split from `loop` so it never self-schedules: BOTH drivers call it — the rAF loop
   *  (normal life) and the starvation-fallback timer (rAF dead in a visible tab, the observed prod
   *  freeze). Everything inside works off wall-clock dt, so the driver swap is invisible to motion. */
  private tick(): void {
    const nowT = performance.now();
    this.lastTickAt = nowT; // the starvation watchdog reads this stamp to know the board is being driven
    const rdt = Math.min(0.05, (nowT - this.lastT) / 1000);
    this.lastT = nowT;
    // ── the game-feel clock (§3.2): lethal slow-mo scales the tween clock, a hitstop freezes it
    // outright. Both are consumed in REAL time HERE (not inside advanceTweens), so a frozen frame can
    // never wedge the clock and headless tests stepping advanceTweens(dt) directly stay deterministic.
    let dt = rdt;
    if (this.slowLeft > 0) {
      this.slowLeft = Math.max(0, this.slowLeft - rdt);
      const RAMP = 0.2; // final 200ms ease back to ×1 (recovery runs on real time, so it can't self-slow)
      const back = this.slowLeft > RAMP ? 0 : 1 - this.slowLeft / RAMP;
      this.timeScale = 0.25 + 0.75 * easeOutQuint(back);
      dt *= this.timeScale;
    } else this.timeScale = 1;
    if (this.freezeLeft > 0) { this.freezeLeft = Math.max(0, this.freezeLeft - rdt); dt = 0; }
    this.advanceTweens(dt);
    // NaN sentinel: a single NaN reaching the camera poisons the projection matrix and with it the
    // whole GPU pass — a frozen picture with the duel still audible, and NOTHING throws in JS, so
    // the guards above can't see it. Catch it here and snap back to the rest pose instead.
    if (!isFinite(this.camera.position.x + this.camera.position.y + this.camera.position.z)) {
      console.error("[arena] camera went non-finite — resetting to rest and dropping camera beats");
      this.camFx = [];
      this.shake = 0;
      this.camera.position.copy(this.camRest);
      this.camera.lookAt(0, 0, CAM_LOOK_Z);
    }
    // ── cosmetic per-frame dressing. Guarded as ONE block: it runs BETWEEN the tween clock and
    // render(), so an uncaught throw anywhere in here would skip render() EVERY frame — the exact
    // frozen-picture-with-live-audio signature, and one the render-fail counter can NEVER see
    // (render() is simply never reached). One glitchy frame of dressing must not starve presentation.
    let handMoving = false;
    try {
    // pulse the selectable card glows + attack swords
    const t = (nowT - this.startT) / 1000;
    const pulse = 0.72 + 0.28 * (0.5 + 0.5 * Math.sin(t * 2.6)); // a calm breathe, not a strobe
    // constant on-screen ATK/DEF size: world-height ∝ distance cancels perspective shrink.
    const tanHalfFov = Math.tan((this.camera.fov * Math.PI) / 360);
    for (const s of this.slots.values()) {
      // (the holographic foil animates itself off the TSL `time` node — nothing to push per frame)
      if (s.glowMat.opacity > 0) {
        // The hovered candidate burns solid + slightly larger; the rest keep breathing.
        const card = s.art.userData.card as QueriedCard | undefined;
        const hov = !!card && this.hoverKey === `${card.controller}:${card.location}:${card.sequence}`;
        s.glowMat.opacity = hov ? 1 : pulse;
        s.glow.scale.setScalar(hov ? 1.12 : 1);
      }
      if (s.sword.visible) { s.swordMat.opacity = 0.85 + 0.15 * Math.sin(t * 5); s.sword.position.y = 0.5 + 0.04 * Math.sin(t * 4); }
      if (s.stat.visible) {
        const dist = this.camera.position.distanceTo(s.stat.getWorldPosition(this.statTmp));
        const hw = ((STAT_PX * 2 * tanHalfFov) / this.vpH) * dist;
        s.stat.scale.set(hw * STAT_ASPECT, hw, 1);
      }
      if (s.link.visible) {
        s.linkMat.opacity = 0.8 + 0.2 * Math.sin(t * 4.4); // gentle gold pulse
        const dist = this.camera.position.distanceTo(s.link.getWorldPosition(this.statTmp));
        const hw = ((28 * 2 * tanHalfFov) / this.vpH) * dist; // constant ~28px on screen
        s.link.scale.set(hw, hw, 1);
      }
    }
    // screen-anchored hand fan (camera-local): animate your foils + LIFT the hovered card straight up
    // a little (Master-Duel style — no zoom, no scale; the CardInfo rail is the magnifier), drawing it
    // over its neighbours. Cards glide to their fan slot, so re-fans after draws/plays are smooth.
    for (const h of this.handMeshes) {
      const hov = this.hoverKey === h.key;
      const target = this.statTmp.copy(h.base);
      // shuffleHand (§4.6): the fan gathers to a stack at its centre and re-deals. handGather is a
      // 0→1→0 scalar blended into every card's target, so a fan REBUILD mid-shuffle just keeps going.
      const gath = this.handGather[h.mine ? "me" : "opp"];
      if (gath > 0.001) {
        target.lerp(this.gatherTmp.set(0, h.mine ? HAND_BASE_Y : OPP_HAND_Y, -HAND_DIST), gath);
        handMoving = true;
      }
      if (hov) target.y += 0.08; // a clean, slight vertical lift, nothing else
      if (!isFinite(target.x + target.y + target.z)) continue; // a NaN target must never enter the fan
      if (h.mesh.position.distanceToSquared(target) > 1e-6) handMoving = true; // fan still gliding → full rate
      h.mesh.position.lerp(target, 0.28);
      h.mesh.renderOrder = hov ? 60 : (h.mesh.userData.ro as number);
      if (h.foil) { h.foil.position.copy(h.mesh.position); h.foil.position.z += 0.01; h.foil.renderOrder = h.mesh.renderOrder + 1; }
    }
    // pile-count badges: hold a constant on-screen size too (so far piles stay readable)
    for (const cs of this.countSprites) {
      const dist = this.camera.position.distanceTo(cs.getWorldPosition(this.statTmp));
      const hw = ((COUNT_PX * 2 * tanHalfFov) / this.vpH) * dist;
      cs.scale.set(hw * COUNT_ASPECT, hw, 1);
    }
    // chain LEVITATION: the activating card RISES off the mat while its link is live and settles back
    // when it resolves — eased both ways (an instant jump read as a glitch), with a slow bob on top.
    const bobT = this.reduced ? 0 : Math.sin(t * 1.2) * 0.04;
    const ease = Math.min(1, dt * 5);
    for (const [key, s] of this.slots) {
      const lev = this.chainZoneKeys.has(key) && s.occupied && !s.exiting;
      if (lev) this.levitated.add(key);
      if (!this.levitated.has(key)) continue;
      const targetY = lev ? 0.3 + bobT : 0.02;
      s.group.position.y += (targetY - s.group.position.y) * ease;
      if (!lev && Math.abs(s.group.position.y - 0.02) < 0.004) {
        s.group.position.y = 0.02;
        this.levitated.delete(key);
      }
    }
    // chain markers: the golden ring rotates slowly over its card (the classic read). Static under
    // reduced motion — the ring + number still mark the link, they just don't turn. A field marker
    // rides its levitating card's height; a pile marker (card + ring) bobs above its stack.
    for (const fx of this.chainFx.values()) {
      if (!this.reduced) {
        if (fx.ring instanceof THREE.Mesh) fx.ring.rotation.z += dt * CHAIN_SPIN;
        else (fx.ring.material as THREE.SpriteMaterial).rotation -= dt * CHAIN_SPIN;
      }
      if (fx.zoneKey) {
        const s = this.slots.get(fx.zoneKey);
        if (s) fx.grp.position.y = s.group.position.y + 0.04;
      } else if (fx.bobBase !== undefined) {
        // pile hover card: ease UP from the stack on arrival, then ride the slow bob
        fx.grp.position.y += (fx.bobBase + bobT - fx.grp.position.y) * ease;
      }
    }
    // pulse legal-zone placement highlights
    if (this.placeActive) {
      const zp = 0.28 + 0.26 * (0.5 + 0.5 * Math.sin(t * 4.2));
      for (const m of this.zoneGroup.children) (m as THREE.Mesh).material && ((((m as THREE.Mesh).material) as THREE.MeshBasicMaterial).opacity = zp);
    }
    } catch (e) {
      // same law as the tween/camera/render guards: log throttled, keep presenting
      if (nowT - this.lastLoopErr > 2000) { this.lastLoopErr = nowT; console.error("[arena] per-frame dressing failed (continuing)", e); }
    }
    if (import.meta.env.DEV) this.nanScan(); // DEV tripwire: any non-finite transform names itself
    // (camera shake + choreography beats are composed inside advanceTweens → applyCamera, so the
    // headless test path — advanceTweens(dt) + render() — exercises the exact same camera code.)
    // Render budget: full rate (up to the quality tier's FPS cap) while anything is actively moving
    // (flights, shake, camera beats, hitstop/slow-mo, intro, a hover change), else throttle to ~30fps —
    // the only idle motion is the slow foil shimmer + glow breathe, which read fine at 30fps. Low tier
    // caps everything at 30fps, roughly halving GPU duty cycle: the main thermal relief on weak laptops.
    const busy = this.tweens.length > 0 || this.camFx.length > 0 || this.shake > 0.001 ||
      this.freezeLeft > 0 || this.slowLeft > 0 || this.introActive || handMoving || this.hoverKey !== this.lastHover;
    this.lastHover = this.hoverKey;
    const minInterval = 1000 / qualityMaxFps(effectiveTier()); // low → ~33ms (30fps), else ~16.7ms (60fps)
    const interval = busy ? minInterval : Math.max(32, minInterval);
    // Half-frame tolerance: rAF ticks jitter around the exact interval, so `elapsed >= interval` skips
    // ~every other frame at the cap — animations juddered at 30-45fps on a 60Hz screen. The tolerance
    // renders every tick the budget allows while still honouring the 30fps throttle.
    if (nowT - this.lastRenderT >= interval - 5) { this.render(); this.lastRenderT = nowT; }
    this.watchdog(); // GPU heartbeat: catches presentation dying silently (no throw, no device.lost)
  }
  private lastRenderT = 0;
  private lastHover: string | null = null;
  private lastLoopErr = 0;

  // ── rAF-starvation watchdog ──────────────────────────────────────────────────
  // THE observed prod freeze (three incidents + one caught live): requestAnimationFrame stops being
  // serviced in a VISIBLE tab while timers, clicks and the play pump keep running — the picture dies
  // mid-animation, the duel stays audible, nothing throws, device.lost never fires, and rAF may
  // resume by itself seconds later. Intervals demonstrably keep firing in this failure mode, so a
  // 500ms interval stands guard: if the loop hasn't been driven for STARVE_MS while the tab is
  // visible, it DRIVES THE BOARD ITSELF at ~30fps and keeps offering rAF a way back (one re-arm per
  // check, single pending handle). A genuinely hidden tab is allowed to pause — never flagged.
  private lastTickAt = performance.now();
  private fallbackDriving = false;
  private starveTimer: ReturnType<typeof setInterval> | undefined;
  private fallbackTimer: ReturnType<typeof setInterval> | undefined;
  private starvedSince = 0; // when the CURRENT starvation began (0 = healthy) — drives the escalation ladder
  private degraded = false; // stage 2 fired: heavy FX shed + DPR dropped for the rest of this arena's life
  private static readonly STARVE_MS = 1500;
  private static readonly DEGRADE_MS = 6000;  // still starved after 6s of timer-driving → shed GPU load
  private static readonly FAILOVER_MS = 14000; // still starved after that → the 2D board takes the duel

  private starveCheck = (): void => {
    if (this.disposed || !this.ready) return;
    if (document.visibilityState !== "visible") { this.lastTickAt = performance.now(); return; } // hidden = legal pause; re-stamp so resume isn't flagged
    if (this.fallbackDriving) {
      // still starved: keep offering rAF the loop back (cancel-then-request → one pending handle)
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(this.loop);
      // ── escalation ladder: timer-driving only cures SCHEDULER starvation. If the true cause is
      // the GPU drowning in OUR OWN work (frames never finish, so the browser stops scheduling),
      // driving harder changes nothing — so shed load, and if even that doesn't bring frames back,
      // hand the duel to the 2D board. The player must NEVER sit at a frozen picture.
      const starvedFor = performance.now() - this.starvedSince;
      if (!this.degraded && starvedFor > DuelArena.DEGRADE_MS) {
        this.degraded = true; // folds into the `reduced` gate: ceremonies/blooms/shake/dim/pools all honour it
        console.error("[arena] still starved after timer-driving — shedding GPU load (effects off, DPR 1)");
        try { this.renderer.setPixelRatio(1); } catch { /* renderer mid-teardown */ }
      } else if (starvedFor > DuelArena.FAILOVER_MS) {
        console.error("[arena] starvation survived load-shedding — handing the duel to the 2D board");
        this.failOver();
      }
      return;
    }
    const gap = performance.now() - this.lastTickAt;
    if (gap <= DuelArena.STARVE_MS) return;
    this.fallbackDriving = true;
    this.starvedSince = performance.now() - gap;
    console.error(`[arena] rAF starved for ${Math.round(gap)}ms with the tab visible — driving the board from a timer at ~30fps (tweens=${this.tweens.length}, camFx=${this.camFx.length}, renderFails=${this.renderFails})`);
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(this.loop); // if the scheduler recovers, the native tick resumes ownership
    this.fallbackTimer = setInterval(() => {
      if (this.disposed || !this.fallbackDriving) { clearInterval(this.fallbackTimer); this.fallbackTimer = undefined; return; }
      if (document.visibilityState !== "visible") return; // pause like rAF would while hidden
      this.tick();
    }, 33);
  };

  resize(): void {
    const r = this.canvas.getBoundingClientRect();
    // The board mounts inside a transform: scale() stage; onMount (and the ResizeObserver, which reads
    // the UN-transformed layout box) can fire while the stage is still collapsed → a degenerate rect
    // that would lock in a 2×2 buffer (black board). Retry next frame until the canvas has real size.
    // ponytail: spins per-frame only while <3px, which happens only mid-mount (the board is unmounted in 2D mode).
    if (r.width < 3 || r.height < 3) { if (!this.disposed) requestAnimationFrame(() => this.resize()); return; }
    // a resize reconfigures the swapchain — void any in-flight GPU heartbeat so the watchdog never
    // counts the reconfigure gap as a dead queue (a stale ACK is ignored via the seq bump)
    this.wdSeq++; this.wdBeatFrame = -1;
    this.vpH = Math.max(1, r.height);
    // ADAPTIVE pixel ratio: the quality-tier cap on normal windows, but scale down on huge ones so the
    // backing store never exceeds ~3.5M px (a 2560×1440 window renders near DPR 1 instead of 8M+ px).
    // Unlit quads upscale cleanly; smoothness beats imperceptible sharpness.
    const budget = Math.sqrt(3_500_000 / Math.max(1, r.width * r.height));
    this.renderer.setPixelRatio(Math.max(1, Math.min(window.devicePixelRatio, qualityDpr(effectiveTier()), budget)));
    this.renderer.setSize(Math.max(1, r.width), Math.max(1, r.height), false);
    if (this.camera) {
      this.camera.aspect = this.aspect();
      this.camera.updateProjectionMatrix();
      this.fitCamera(); // re-solve the framing so the mat always fits the new canvas aspect
    }
  }

  dispose(): void {
    this.disposed = true;
    clearTimeout(this.initTimer);
    clearInterval(this.starveTimer);
    clearInterval(this.fallbackTimer);
    this.clearAimLine();
    cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerleave", this.onPointerLeave);
    this.canvas.removeEventListener("click", this.onClick);
    this.texCache.forEach((t) => t.dispose());
    this.chainNumTex.forEach((t) => t.dispose());
    this.chainRingTex?.dispose();
    this.portalTexCache.forEach((t) => t.dispose());
    if (this.chainDim) { this.chainDim.mat.dispose(); this.chainDim.mesh.geometry.dispose(); }
    for (const p of this.chainPools.values()) { p.mat.dispose(); p.mesh.geometry.dispose(); }
    this.lockTex?.dispose();
    this.myBackTex.dispose();
    this.oppBackTex.dispose();
    this._missingTex?.dispose();
    for (const s of this.slots.values()) { s.foilMat.dispose(); s.foil.geometry.dispose(); }
    for (const h of this.handMeshes) { h.foilMat?.dispose(); (h.mesh.material as THREE.Material).dispose?.(); h.mesh.geometry.dispose(); }
    (this.matMesh.material as THREE.MeshBasicMaterial).map?.dispose();
    this.renderer.dispose();
  }
}
